import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Inbox, CheckCircle2, XCircle, Clock, Eye, X, HelpCircle, Search, ChevronRight, ChevronDown, RefreshCw, CalendarDays } from "lucide-react";
import { AttachmentChip } from "@/components/AttachmentsField";
import { useAuth } from "@/contexts/AuthContext";
import { QueryRaiseInline, QueryRespondInline, QueryHistory } from "@/components/ApprovalQuery";
import UserLabel, { formatUser, useUserDirectory } from "@/components/UserLabel";
import ApproveEstimateDialog from "@/components/ApproveEstimateDialog";
import { BoqPoDeductionNote } from "@/components/BoqPoDeductionNote";
import { useFormTab } from "@/hooks/useFormTab";
import { triggerMastersRefresh } from "@/hooks/useMastersRefresh";
import ProjectWorkspace from "@/components/ProjectWorkspace";
import { InvoiceModal } from "@/pages/InvoicePage";

const MODULE_LABEL = {
  estimate: "Estimate",
  sales_invoice: "Sales Invoice",
  sales_credit_note: "Sales Credit Note",
  sales_debit_note: "Sales Debit Note",
  purchase_invoice: "Purchase Invoice",
  purchase_order: "Purchase Order",
  issue_voucher: "Issue Voucher",
  return_voucher: "Return Voucher",
  material_transfer_voucher: "Material Transfer",
  warehouse_transfer: "Warehouse Transfer",
  expense_voucher: "Expense Voucher",
  payment: "Payment",
  payment_receipt: "Payment Receipt",
  store_rent: "Store Rent Payment",
  salary_payment: "Salary Payment",
  general_payment: "General Payment",
  bill_payment_voucher: "Bill Payment Voucher",
  vendor_advance: "Vendor Advance",
  project_budget: "Project Budget",
  // Sprint 4.11.17 — Vendor master approval workflow
  vendor_master: "Vendor Master",
  stock_adjustment_voucher: "Stock Adjustment Voucher",
  // Sprint 2026-Feb / JV Phases 2A/2B — Journal Vouchers
  journal_voucher_eb: "Journal Voucher (External Bill)",
  journal_voucher_fc: "Journal Voucher (Foreign Currency)",
  journal_voucher_oi: "Journal Voucher (Other Income)",
  po_cancellation: "PO Cancellation",
  // Jul-2026 — Bank Reconciliation Exception Approval
  bank_recon_exception: "Bank Recon Exception",
  // Jul-2026 — Batch Scrap Sale voucher
  scrap_sale: "Scrap Sale Voucher",
  // Aug-2026 — pre-deployment approval-coverage audit: registered so the
  // inbox never shows a raw module key for these (Phase-23 registered
  // modules; JV-OE actively flows via /journal-vouchers).
  journal_voucher_oe: "Journal Voucher (Other Expenses)",
  stock_transfer: "Stock Transfer",
  jms_completion: "JMS Completion",
  asset_scrap: "Asset Scrap",
  send_to_repair: "Asset Send to Repair",
  asset_mark_ready: "Asset Repair Mark-Ready",
  // Aug-2026 — Phase 1: Project Scope & BOQ
  project_boq: "Project BOQ",
  // Aug-2026 — Phase 2A: Project Purchase Requisition
  project_purchase_requisition: "Project PR",
  // Jun-2026 — Phase 3C: Project GRN
  project_grn: "Project GRN",
  // Jun-2026 — Labour Payment Request (Ops Project day-wise wages)
  labour_payment: "Labour Payment Request",
  // Jun-2026 — PO Payment Request (pseudo-module: live PPR rows in My Vouchers)
  po_payment_request: "PO Payment Request",
};

const MODULE_API = {
  estimate: "/estimates",
  sales_invoice: "/sales-invoices",
  sales_credit_note: "/sales-credit-notes",
  sales_debit_note: "/sales-debit-notes",
  purchase_invoice: "/purchase-invoices",
  purchase_order: "/purchase-orders",
  issue_voucher: "/issue-vouchers",
  return_voucher: "/return-vouchers",
  material_transfer_voucher: "/material-transfers",
  // Bug fix 2026-Feb-06 — user reported "View not working, no documents
  // available" on Warehouse-to-Warehouse transfer approvals. The
  // approval request records `module="warehouse_transfer"` but the
  // frontend map was missing this key so the modal fell through the
  // `if (!base)` guard. Endpoint lives at /api/warehouse-transfers/{id}.
  warehouse_transfer: "/warehouse-transfers",
  // Same class of bug — Expense Voucher / JV-EB / JV-FC approvals
  // record their canonical module string but the map was silent.
  expense_voucher: "/expense-vouchers",
  journal_voucher_eb: "/journal-vouchers",
  journal_voucher_fc: "/journal-vouchers",
  journal_voucher_oi: "/journal-vouchers",
  po_cancellation: "/purchase-orders",
  payment_receipt: "/payment-receipts",
  store_rent: "/store-rent-payments",
  salary_payment: "/salary-payments",
  general_payment: "/general-payments",
  bill_payment_voucher: "/bill-payment-vouchers",
  vendor_advance: "/vendor-advances",
  project_budget: "/project-budgets/bundle",
  vendor_master: "/vendors",
  stock_adjustment_voucher: "/stock-adjustments",
  // Jul-2026 — Bank Reconciliation Exception Approval
  bank_recon_exception: "/bank-recon/exceptions",
  // Aug-2026 — Phase 1: Project Scope & BOQ (flat lookup, commercially scrubbed)
  project_boq: "/project-boq/versions",
  // Aug-2026 — pre-deployment audit: scrap_sale approvals exist in production
  // but View fell through to "viewer not available"; JV-OE shares the JV endpoint.
  scrap_sale: "/scrap-sales",
  journal_voucher_oe: "/journal-vouchers",
  // Aug-2026 — Phase 2A: Project PR (flat lookup, commercially scrubbed)
  project_purchase_requisition: "/project-pr",
  // Jun-2026 — Phase 3C: Project GRN (flat lookup, values scrubbed)
  project_grn: "/project-grn",
  // Jun-2026 — Labour Payment Request (flat lookup; view-or-approve gated)
  labour_payment: "/labour-payment-requests",
};

// Jul-2026 — Accounting Period is a first-class decision factor for
// approvers. "YYYY-MM" → "Jul 2026" for display.
function fmtPeriod(pk) {
  if (!/^\d{4}-\d{2}/.test(pk || "")) return pk || "";
  const [y, m] = pk.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "short", year: "numeric" });
}

export default function ApprovalsInboxPage() {
  // Subscribe to user-directory refreshes so `formatUser(...)` calls
  // inside `DocSummary` / `FieldCell` re-render as soon as the
  // `{email → name}` map hydrates on first mount.
  useUserDirectory();
  const [list, setList] = useState([]);
  // Jun-2026 — PI payment requests assigned to me (cross-project intimation).
  const [payReqs, setPayReqs] = useState([]);
  const [myPayReqs, setMyPayReqs] = useState([]); // PO payment requests I raised — live status (My Vouchers tab)
  const [payReqReject, setPayReqReject] = useState({ id: "", reason: "", busy: false });
  const [payReqView, setPayReqView] = useState(null); // row → detail modal
  const openFormTab = useFormTab();
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState({});
  const [viewing, setViewing] = useState(null); // {request, doc}
  const [viewBusy, setViewBusy] = useState(null); // request.id while its doc loads
  const [tab, setTab] = useState("pending"); // pending | hold | history
  // Sub-tab inside PENDING — bifurcates the actionable pool:
  //   immediate = requests at the user's current_level (your turn to act)
  //   upcoming  = requests where the user is a HIGHER-level approver still
  //               waiting for one or more lower levels to clear first.
  // Dashboard pending count tracks only IMMEDIATE + HOLD.
  const [pendingSub, setPendingSub] = useState("immediate"); // immediate | upcoming
  // Sprint 4.9 — tab counts (rendered in brackets next to each tab label).
  // Pending = status="Pending" rows the current user can act on.
  // Hold    = status="Query Raised" rows in the same actionable scope.
  // History = decided rows (Approved | Rejected) visible to the current user.
  const [counts, setCounts] = useState({ pending: 0, pending_immediate: 0, pending_upcoming: 0, hold: 0, history: 0, mine: 0 });
  const [queryOpenFor, setQueryOpenFor] = useState(null); // request id for which the raise-query inline is open
  const [search, setSearch] = useState("");
  const [filterModule, setFilterModule] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [selected, setSelected] = useState(() => new Set()); // request ids selected for bulk action
  const [bulkBusy, setBulkBusy] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set()); // request ids whose detail panel is open
  const [deciding, setDeciding] = useState(() => new Set()); // request ids with an approve/reject POST in flight

  // Jun-2026 (perf) — optimistic removal after a decision: the row leaves the
  // visible list + counts immediately; the silent background load() converges
  // the real state a moment later.
  const removeRowLocally = (rid) => {
    let wasPending = false;
    setList((prev) => {
      const row = prev.find((r) => r.id === rid);
      wasPending = !!row && row.status === "Pending";
      return prev.filter((r) => r.id !== rid);
    });
    setRawRows((prev) => prev.filter((r) => r.id !== rid));
    setSelected((prev) => { const n = new Set(prev); n.delete(rid); return n; });
    setCounts((c) => wasPending ? {
      ...c,
      pending: Math.max(0, (c.pending || 0) - 1),
      pending_immediate: pendingSub === "immediate" ? Math.max(0, (c.pending_immediate || 0) - 1) : c.pending_immediate,
      pending_upcoming:  pendingSub === "upcoming"  ? Math.max(0, (c.pending_upcoming  || 0) - 1) : c.pending_upcoming,
      history: (c.history || 0) + 1,
    } : c);
  };
  const [rawRows, setRawRows] = useState([]); // union of /pending + /all — for deep-link tab resolution
  // Jun-2026 (perf) — raw endpoint payloads live in state so tab switches
  // derive the visible list instantly instead of refetching with a spinner.
  const [pendingRowsAll, setPendingRowsAll] = useState([]);
  const [allRowsAll, setAllRowsAll] = useState([]);
  const loadedOnceRef = useRef(false);
  // Sprint 2026-Feb-13 — Estimate approval enhancement dialog.
  // When set, ApproveEstimateDialog opens for {request, estimate}.
  const [approveEstFor, setApproveEstFor] = useState(null);
  const { user } = useAuth();
  const uid = user?.id || "";
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkReqId = searchParams.get("req") || "";
  const deepLinkConsumedRef = useRef(false);
  // A new Take-Action click (different or repeated ?req=) re-arms the deep link
  useEffect(() => { deepLinkConsumedRef.current = false; }, [deepLinkReqId]);

  const toggleExpanded = (rid) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(rid)) next.delete(rid); else next.add(rid);
    return next;
  });

  // Jun-2026 — shared by the payment-request row button and its View modal.
  const openPayReqProject = async (r) => {
    try {
      const [p, ops] = await Promise.all([
        api.get(`/projects/${r.project_id}`),
        api.get("/project-ops/status").catch(() => ({ data: {} })),
      ]);
      openFormTab({
        path: `/projects/workspace/${r.project_id}`,
        label: `${p.data.project_code || "Project"} · Workspace`,
        parent: "/projects",
        render: ({ close }) => (
          <ProjectWorkspace project={p.data} opsGlobalOn={!!ops.data?.global_enabled} onClose={close} />
        ),
      });
    } catch {
      toast.error("You don't have access to this project — ask an admin to add you to its Authorized Users.");
    }
  };

  // Jun-2026 — "Create PI" shortcut: opens a New Purchase Invoice tab
  // pre-filled from the request's PO (remaining qty) + linked PPR number.
  const createPiFromPayReq = (r) => openFormTab({
    path: `/purchase-invoices/new/ppr-${r.request_number}`,
    label: `New Invoice · ${r.request_number}`,
    parent: "/purchase-invoices",
    onSaved: load,
    render: ({ close, saved, viewList }) => (
      <InvoiceModal type="purchase" invoice={null}
        prefilled={{ kind: "FROM_PPR", request: r }}
        onClose={close} onSaved={saved} onViewList={viewList} />
    ),
  });

  const load = async (opts = {}) => {
    // Jun-2026 (perf) — silent mode: background re-sync after an optimistic
    // decision. No spinner, no list flash; state just converges.
    if (!opts.silent) setLoading(true);
    try {
      // Always fetch both endpoints so the tab counts stay in sync no matter
      // which tab is currently active.
      const [pendingRes, allRes] = await Promise.all([
        api.get("/approvals/pending"),
        api.get("/approvals/all"),
      ]);
      api.get("/po-payment-requests/assigned-to-me")
        .then((r) => setPayReqs(Array.isArray(r.data) ? r.data : []))
        .catch(() => setPayReqs([]));
      api.get("/po-payment-requests/raised-by-me")
        .then((r) => setMyPayReqs(Array.isArray(r.data) ? r.data : []))
        .catch(() => setMyPayReqs([]));
      setPendingRowsAll(pendingRes.data || []);
      setAllRowsAll(allRes.data || []);
      loadedOnceRef.current = true;
    } finally {
      setLoading(false);
    }
  };

  // Jun-2026 (perf) — the visible rows + tab counts are DERIVED instantly
  // from the in-memory row sets, so switching tabs never waits on the API.
  useEffect(() => {
    const pendingList = pendingRowsAll;
    const allList     = allRowsAll;
    // Raw union (pending wins on id clash) — used by the deep-link resolver
    // to locate a request regardless of which tab it currently lives on.
    const seen = new Set(pendingList.map((r) => r.id));
    setRawRows([...pendingList, ...allList.filter((r) => !seen.has(r.id))]);

    // Tab-specific row set
    let visible = [];
    if (tab === "pending") {
      const allPending = pendingList.filter(r => r.status === "Pending");
      // Default kind to "immediate" so rows from older backend builds
      // (pre-bifurcation) keep showing up as actionable.
      visible = allPending.filter(r => (r.kind || "immediate") === pendingSub);
    } else if (tab === "hold") {
      visible = pendingList.filter(r => r.status === "Query Raised");
    } else if (tab === "mine") {
      // Everything the user RAISED, any status — Pending rows show who
      // they're waiting on so the raiser can chase.
      const seenMine = new Set(allList.map((r) => r.id));
      visible = [...allList, ...pendingList.filter((r) => !seenMine.has(r.id))]
        .filter(r => r.raised_by === uid);
    } else {
      visible = allList.filter(r => r.status === "Approved" || r.status === "Rejected");
    }
    setList(visible);

    const pendingRows = pendingList.filter(r => r.status === "Pending");
    const immediateCount = pendingRows.filter(r => (r.kind || "immediate") === "immediate").length;
    const upcomingCount  = pendingRows.filter(r => r.kind === "upcoming").length;
    const seenMineC = new Set(allList.map((r) => r.id));
    setCounts({
      pending: immediateCount + upcomingCount,
      pending_immediate: immediateCount,
      pending_upcoming:  upcomingCount,
      hold:    pendingList.filter(r => r.status === "Query Raised").length,
      history: allList.filter(r => r.status === "Approved" || r.status === "Rejected").length,
      mine: [...allList, ...pendingList.filter((r) => !seenMineC.has(r.id))]
        .filter(r => r.raised_by === uid).length,
    });
  }, [tab, pendingSub, pendingRowsAll, allRowsAll, uid]);

  // Tab switches render instantly from state; a SILENT background re-sync
  // keeps the data fresh (full-screen spinner only on the very first load).
  useEffect(() => { load({ silent: loadedOnceRef.current }); setSelected(new Set()); /* eslint-disable-next-line */ }, [tab, pendingSub]);

  // Deep-link support: ?req=<id> auto-expands & scrolls to the target request.
  // Switches to the History tab automatically if the request isn't in Pending.
  useEffect(() => {
    if (!deepLinkReqId || deepLinkConsumedRef.current) return;
    if (loading) return;
    const found = list.find((r) => r.id === deepLinkReqId);
    if (!found) {
      // The request may live on ANY tab — Pending (immediate/upcoming),
      // Hold (Query Raised) or History — resolve the tab from raw rows.
      const src = rawRows.find((r) => r.id === deepLinkReqId);
      if (src) {
        const targetTab = src.status === "Pending" ? "pending"
          : src.status === "Query Raised" ? "hold" : "history";
        const targetSub = src.kind || "immediate";
        if (targetTab !== tab || (targetTab === "pending" && targetSub !== pendingSub)) {
          if (targetTab === "pending") setPendingSub(targetSub);
          setTab(targetTab);
        }
        // We know the request exists — wait for the target tab's list to
        // load (avoids a false "not found" during the tab-switch refetch).
        return;
      } else if (tab !== "history") {
        setTab("history");
        return;
      }
      toast.error("Approval request not found or no longer visible to you");
      deepLinkConsumedRef.current = true;
      setSearchParams({}, { replace: true });
      return;
    }
    deepLinkConsumedRef.current = true;
    setExpanded((prev) => {
      const next = new Set(prev);
      next.add(deepLinkReqId);
      return next;
    });
    setTimeout(() => {
      const el = document.querySelector(`[data-approval-row="${deepLinkReqId}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkReqId, loading, list, tab, pendingSub, rawRows]);

  async function viewDocument(req) {
    const base = MODULE_API[req.module];
    if (!base) { toast.error("Document viewer not available for this module"); return; }
    setViewBusy(req.id);
    try {
      const r = await api.get(`${base}/${req.document_id}`);
      // Perf (Sep-2026, "View is lagging") — open the modal after ONE round
      // trip; the context panels below hydrate in the background instead of
      // blocking the modal (previously 3 sequential awaits).
      setViewing({ request: req, doc: r.data, opsPay: null, projCtx: null });
      const hydrate = (patch) =>
        setViewing(v => (v && v.request?.id === req.id) ? { ...v, ...patch } : v);
      // Aug-2026 — Ops Project approvals: show the project's PO-Payment
      // totals to the approver (silently hidden for non-ops projects).
      const OPS_PAY_MODULES = ["purchase_order", "purchase_invoice",
        "bill_payment_voucher", "vendor_advance", "po_cancellation"];
      if (OPS_PAY_MODULES.includes(req.module) && r.data?.project_id) {
        api.get(`/projects/${r.data.project_id}/po-payments-summary`)
          .then(res => hydrate({ opsPay: res.data })).catch(() => {});
      }
      // Jun-2026 (user spec) — PI approvers see the project's budget context
      // (total expenditure + total sales) before deciding.
      if (req.module === "purchase_invoice" && r.data?.project_id) {
        api.get(`/approvals/project-context/${r.data.project_id}`)
          .then(res => hydrate({ projCtx: res.data })).catch(() => {});
      }
    } catch (e) {
      const status = e?.response?.status;
      // Orphaned approval: source document was deleted while the approval
      // record remained. Don't block the user — open the modal in
      // "orphan" mode so they can still see the audit trail.
      if (status === 404) {
        setViewing({
          request: req,
          doc: null,
          orphan: true,
        });
        return;
      }
      toast.error(e?.response?.data?.detail || "Could not load document");
    } finally {
      setViewBusy(null);
    }
  }

  async function decide(rid, action) {
    // Sprint 2026-Feb-13 — For an Estimate approval, route through the
    // enhancement dialog (4 kinds: Just Approve / Work Date / Voucher
    // Details / Both). All other modules + Reject use the original
    // fast-path so existing UX is unchanged.
    if (action === "approve") {
      const req = list.find(r => r.id === rid);
      if (req && String(req.module || "").toLowerCase() === "estimate") {
        try {
          const { data: est } = await api.get(`/estimates/${req.document_id}`);
          setApproveEstFor({ request: req, estimate: est });
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not load estimate");
        }
        return;
      }
    }
    // Jun-2026 (perf) — optimistic UX: per-row busy state while the POST is
    // in flight, then remove the row INSTANTLY on success and re-sync the
    // tab counts silently in the background (no full-list loading flash).
    setDeciding((prev) => new Set(prev).add(rid));
    try {
      const { data: d } = await api.post(`/approvals/${rid}/${action}`, { comment: comment[rid] || "" });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      if (!d || d.status !== "Pending") removeRowLocally(rid);
      // Jun-2026 — broadcast so mounted payment screens (Bill Payment
      // vendors/outstandings, voucher forms) re-pull without a full reload.
      triggerMastersRefresh();
      load({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
      // 409 = someone else actioned it concurrently — re-sync to drop the row.
      if (e?.response?.status === 409) load({ silent: true });
    } finally {
      setDeciding((prev) => { const n = new Set(prev); n.delete(rid); return n; });
    }
  }

  /* ===== Bulk action helpers ===== */
  function toggleSelected(rid) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid); else next.add(rid);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  async function bulkDecide(action) {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`${action === "approve" ? "Approve" : "Reject"} ${ids.length} selected request${ids.length === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    const remarks = comment["__bulk__"] || "";
    let ok = 0, fail = 0;
    const succeeded = [];
    // Run sequentially so server-side audit logs land in deterministic order.
    for (const rid of ids) {
      try {
        const { data: d } = await api.post(`/approvals/${rid}/${action}`, { comment: remarks });
        ok += 1;
        if (!d || d.status !== "Pending") succeeded.push(rid);
      } catch {
        fail += 1;
      }
    }
    setBulkBusy(false);
    clearSelection();
    if (ok) toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${ok} request${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}`);
    else toast.error(`${action} failed for all ${fail} selected request${fail === 1 ? "" : "s"}`);
    if (ok) triggerMastersRefresh();
    // Jun-2026 (perf) — succeeded rows vanish instantly; silent re-sync.
    succeeded.forEach(removeRowLocally);
    load({ silent: true });
  }

  // Helpers that derive the per-row UI mode for the current viewer.
  function openQueryFor(req) {
    return (req.queries || []).find(q => q.status === "Open");
  }
  function shouldShowRespondForm(req) {
    const q = openQueryFor(req);
    return !!q && q.raised_to === uid;
  }
  function shouldShowApproveButtons(req) {
    if (req.status !== "Pending") return false;
    if (req.raised_by === uid) return false; // creators never see approve on their own request
    if (req.kind === "upcoming") return false; // higher-level approver, lower level hasn't cleared yet
    return true;
  }

  // Status options offered for the dropdown — each tab limits to its native
  // statuses so the picker stays uncluttered. With pagination + counts the
  // dropdown is rarely needed inside Pending / Hold (everything in those
  // tabs already has that status).
  const STATUS_OPTIONS = tab === "pending"
    ? ["ALL", "Pending"]
    : tab === "hold"
    ? ["ALL", "Query Raised"]
    : tab === "mine"
    ? ["ALL", "Pending", "Query Raised", "Approved", "Rejected"]
    : ["ALL", "Approved", "Rejected"];

  // Derive distinct modules that actually exist in the current list so the
  // voucher-type dropdown only shows what's available (avoids showing 20 modules
  // when the user only has 3 in front of them).
  const moduleOptions = useMemo(() => {
    const set = new Set();
    list.forEach(r => set.add(r.module));
    if (tab === "mine" && myPayReqs.length > 0) set.add("po_payment_request");
    return ["ALL", ...Array.from(set).sort()];
  }, [list, tab, myPayReqs]);

  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter(r => {
      if (filterModule !== "ALL" && r.module !== filterModule) return false;
      if (filterStatus !== "ALL" && r.status !== filterStatus) return false;
      if (!q) return true;
      // Full-text search across the most user-visible fields + query messages
      const hay = [
        r.document_number,
        r.module,
        MODULE_LABEL[r.module],
        r.raised_by_email,
        r.status,
        ...(r.queries || []).flatMap(qq => [qq.message, qq.response, qq.raised_by_email, qq.raised_to_email]),
        ...(r.history || []).flatMap(h => [h.user_email, h.comment, h.action]),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [list, search, filterModule, filterStatus]);

  // My Vouchers — the PPR panel obeys the same filter bar (module / status / search).
  const filteredPayReqs = useMemo(() => {
    if (filterModule !== "ALL" && filterModule !== "po_payment_request") return [];
    const q = search.trim().toLowerCase();
    return myPayReqs.filter(r => {
      if (filterStatus !== "ALL" && r.status !== filterStatus) return false;
      if (!q) return true;
      const hay = [r.request_number, r.po_number, r.vendor_name, r.project_code,
        r.project_name, r.status, r.pi_number, r.assigned_to_display,
        r.payment_status, r.rejection_reason].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [myPayReqs, search, filterModule, filterStatus]);

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-[1400px]">
      <Toaster richColors position="top-right" />
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <Inbox size={24} className="text-[#F97316] shrink-0 md:w-7 md:h-7" />
        <div className="min-w-0 flex-1">
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">Approvals Inbox</h1>
          <div className="text-xs md:text-sm text-slate-500">
            {tab === "pending" && "Requests waiting for your action"}
            {tab === "hold"    && "Requests on hold pending clarification"}
            {tab === "history" && "Decided requests (history)"}
            {tab === "mine"    && "Vouchers you raised — every status, with who they're pending with"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => { load(); toast.success("Refreshed"); }}
          disabled={loading}
          data-testid="approvals-refresh"
          title="Reload requests from server"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider border border-slate-300 bg-white text-slate-700 hover:border-[#F97316] hover:text-[#F97316] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      <div className="flex gap-1 mb-2 border-b border-slate-200 overflow-x-auto">
        {[
          ["pending", "Pending", counts.pending],
          ["hold",    "Hold",    counts.hold],
          ["history", "History", counts.history],
          ["mine",    "My Vouchers", counts.mine],
        ].map(([k, l, n]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 sm:px-5 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px whitespace-nowrap ${
              tab === k ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            {l}
            <span className={`ml-1.5 tabular-nums font-extrabold ${
              tab === k ? "text-[#F97316]" : "text-slate-400"
            }`} data-testid={`tab-${k}-count`}>({n})</span>
          </button>
        ))}
      </div>

      {/* Sub-tab inside Pending — bifurcates Immediate (your turn) vs
          Upcoming (lower-level approval still in progress) */}
      {tab === "pending" && (
        <div className="flex gap-1 mb-4 -mt-1" data-testid="pending-subtabs">
          {[
            ["immediate", "Immediate", counts.pending_immediate, "Your turn to act"],
            ["upcoming",  "Upcoming",  counts.pending_upcoming,  "Waiting for lower-level approval"],
          ].map(([k, l, n, hint]) => (
            <button key={k}
              data-testid={`pending-subtab-${k}`}
              onClick={() => setPendingSub(k)}
              title={hint}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5 ${
                pendingSub === k
                  ? "bg-[#F97316] text-white"
                  : "bg-white border border-slate-300 text-slate-600 hover:border-[#F97316]"
              }`}>
              {l}
              <span className={`tabular-nums px-1 text-[10px] ${
                pendingSub === k ? "bg-white/25" : "bg-slate-100"
              }`} data-testid={`pending-subtab-${k}-count`}>{n}</span>
            </button>
          ))}
          <div className="ml-auto text-[10px] text-slate-500 self-center italic">
            {pendingSub === "upcoming"
              ? "Read-only — you'll be able to act once the lower level approves."
              : "Action required — approve / reject each request below."}
          </div>
        </div>
      )}

      {/* Filter row — search + voucher (module) + status */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr,180px,180px] gap-2 mb-4">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by document #, raiser email, query text…"
            data-testid="approvals-search"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 text-xs bg-white focus:border-[#F97316] focus:outline-none"
          />
        </div>
        <select
          value={filterModule}
          onChange={e => setFilterModule(e.target.value)}
          data-testid="approvals-filter-module"
          className="px-3 py-2 border border-slate-300 text-xs bg-white focus:border-[#F97316] focus:outline-none"
        >
          {moduleOptions.map(m => (
            <option key={m} value={m}>{m === "ALL" ? "All Vouchers" : (MODULE_LABEL[m] || m)}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          data-testid="approvals-filter-status"
          className="px-3 py-2 border border-slate-300 text-xs bg-white focus:border-[#F97316] focus:outline-none"
        >
          {STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>{s === "ALL" ? "All Statuses" : s}</option>
          ))}
        </select>
      </div>

      {(filterModule !== "ALL" || filterStatus !== "ALL" || search.trim()) && (
        <div className="text-[11px] text-slate-500 mb-2 flex flex-wrap items-center gap-2" data-testid="approvals-filter-summary">
          Showing <strong className="text-slate-800">{filteredList.length}</strong> of <strong>{list.length}</strong>
          <button
            type="button"
            onClick={() => { setSearch(""); setFilterModule("ALL"); setFilterStatus("ALL"); }}
            data-testid="approvals-clear-filters"
            className="text-[#F97316] hover:underline"
          >Clear filters</button>
        </div>
      )}

      {/* Bulk action bar — visible whenever the current user has selected at least one bulk-able row */}
      {(() => {
        const bulkable = filteredList.filter(shouldShowApproveButtons);
        const selectedCount = bulkable.filter(r => selected.has(r.id)).length;
        const allChecked = bulkable.length > 0 && selectedCount === bulkable.length;
        if (bulkable.length === 0) return null;
        return (
          <div className="border border-slate-200 bg-slate-50 px-3 py-2 mb-3 flex flex-wrap items-center gap-3" data-testid="approvals-bulk-bar">
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                data-testid="approvals-bulk-select-all"
                checked={allChecked}
                ref={el => { if (el) el.indeterminate = selectedCount > 0 && !allChecked; }}
                onChange={(e) => {
                  if (e.target.checked) setSelected(new Set(bulkable.map(r => r.id)));
                  else clearSelection();
                }}
                className="w-4 h-4 accent-[#F97316]"
              />
              <span className="font-bold">
                {selectedCount > 0 ? `${selectedCount} selected` : `Select all bulk-able (${bulkable.length})`}
              </span>
            </label>
            <input
              data-testid="approvals-bulk-comment"
              value={comment["__bulk__"] || ""}
              onChange={e => setComment(c => ({ ...c, __bulk__: e.target.value }))}
              placeholder="Optional bulk remark (applied to every selected request)…"
              disabled={selectedCount === 0 || bulkBusy}
              className="flex-1 min-w-[200px] text-xs px-2 py-1.5 border border-slate-300 focus:border-[#F97316] focus:outline-none disabled:bg-slate-100"
            />
            <button
              data-testid="approvals-bulk-approve"
              disabled={selectedCount === 0 || bulkBusy}
              onClick={() => bulkDecide("approve")}
              className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider px-3 py-2"
            >
              <CheckCircle2 size={13} /> {bulkBusy ? "Working…" : `Approve${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
            </button>
            <button
              data-testid="approvals-bulk-reject"
              disabled={selectedCount === 0 || bulkBusy}
              onClick={() => bulkDecide("reject")}
              className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider px-3 py-2"
            >
              <XCircle size={13} /> {bulkBusy ? "Working…" : `Reject${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
            </button>
            {selectedCount > 0 && (
              <button onClick={clearSelection} className="text-[11px] text-slate-500 hover:text-slate-800 underline">
                Clear
              </button>
            )}
          </div>
        );
      })()}

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && filteredList.length === 0 && !(tab === "mine" && filteredPayReqs.length > 0) && (
        <div className="border border-slate-200 bg-slate-50 p-10 text-center">
          <CheckCircle2 size={32} className="text-emerald-600 mx-auto mb-2" />
          <div className="text-sm font-semibold text-slate-700">{list.length === 0 ? "All caught up" : "No matches"}</div>
          <div className="text-xs text-slate-500 mt-1">
            {list.length === 0
              ? "No pending approvals waiting on you."
              : "Try clearing the filters or widening the search."}
          </div>
        </div>
      )}

      {/* Jun-2026 — PI payment requests assigned to me (intimation from the
          ops project's Vendor Management → Payment Requests). Not part of the
          approval-matrix flow, so surfaced as a dedicated amber panel. */}
      {tab === "mine" && filteredPayReqs.length > 0 && (
        <div className="border border-sky-300 bg-sky-50/50 mb-3" data-testid="my-payreq-panel">
          <div className="px-4 py-2 border-b border-sky-200 flex items-center gap-2">
            <Clock size={13} className="text-sky-700" />
            <span className="text-[11px] uppercase tracking-wider font-black text-sky-800">
              PO Payment Requests raised by you ({filteredPayReqs.length}{filteredPayReqs.length !== myPayReqs.length ? ` of ${myPayReqs.length}` : ""})
            </span>
            <span className="text-[10px] text-sky-700">— live status, updates on every refresh</span>
          </div>
          {filteredPayReqs.map((r) => (
            <div key={r.id} className="border-b border-sky-100 last:border-b-0" data-testid={`my-payreq-row-${r.request_number}`}>
              <div className="px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-xs font-black text-slate-800">{r.request_number}</span>
                {r.status === "Pending" && (
                  <span data-testid={`my-payreq-status-${r.request_number}`} className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300">
                    Pending{r.assigned_to_display ? ` · with ${r.assigned_to_display}` : ""}
                  </span>
                )}
                {r.status === "PI Created" && (
                  <span data-testid={`my-payreq-status-${r.request_number}`} className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300">
                    PI Created{r.pi_number ? ` · ${r.pi_number}` : ""}
                  </span>
                )}
                {r.status === "PI Approved" && (
                  <span data-testid={`my-payreq-status-${r.request_number}`} className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-indigo-100 text-indigo-800 border border-indigo-300">
                    PI Approved{r.pi_number ? ` · ${r.pi_number}` : ""}
                  </span>
                )}
                {(r.status === "PI Created" || r.status === "PI Approved") && r.payment_status && (
                  <span data-testid={`my-payreq-paystate-${r.request_number}`} className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 border ${
                    r.payment_status === "Paid" ? "bg-emerald-600 text-white border-emerald-700"
                    : r.payment_status === "Partially Paid" ? "bg-amber-100 text-amber-800 border-amber-300"
                    : "bg-sky-100 text-sky-800 border-sky-300"}`}>
                    {r.payment_status}
                  </span>
                )}
                {r.status === "Rejected" && (
                  <span data-testid={`my-payreq-status-${r.request_number}`} className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-rose-100 text-rose-800 border border-rose-300">
                    Rejected
                  </span>
                )}
                {r.status === "Cancelled" && (
                  <span data-testid={`my-payreq-status-${r.request_number}`} className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-slate-100 text-slate-600 border border-slate-300">
                    Cancelled
                  </span>
                )}
                <span className="text-[11px] text-slate-600" title={r.project_name}>
                  {r.project_code}{r.project_name ? ` — ${r.project_name}` : ""}
                </span>
                <span className="text-[11px] font-mono text-slate-600">{r.po_number}</span>
                <span className="text-[11px] text-slate-500">{r.vendor_name}</span>
                {r.requested_amount != null && Number(r.requested_amount) > 0 && (
                  <span className="text-[11px] font-bold tabular-nums text-slate-700">
                    ₹ {Number(r.requested_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </span>
                )}
                <span className="text-[10px] text-slate-400">
                  raised {(r.created_at || "").slice(0, 10)}
                  {r.updated_at && r.updated_at !== r.created_at ? ` · updated ${(r.updated_at || "").slice(0, 10)}` : ""}
                </span>
                <button
                  data-testid={`my-payreq-open-${r.request_number}`}
                  onClick={() => openPayReqProject(r)}
                  className="ml-auto text-[10px] font-bold uppercase tracking-wider text-sky-800 border border-sky-400 hover:bg-sky-100 px-2.5 py-1 transition-colors">
                  Open Project →
                </button>
              </div>
              {r.status === "Rejected" && r.rejection_reason && (
                <div className="px-4 pb-2 -mt-1 text-[10px] text-rose-700 italic" data-testid={`my-payreq-reason-${r.request_number}`}>
                  Reason: {r.rejection_reason}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "pending" && payReqs.length > 0 && (
        <div className="border border-amber-300 bg-amber-50/60 mb-3" data-testid="payreq-inbox-panel">
          <div className="px-4 py-2 border-b border-amber-200 flex items-center gap-2">
            <Clock size={13} className="text-amber-700" />
            <span className="text-[11px] uppercase tracking-wider font-black text-amber-800">
              PI Payment Requests assigned to you ({payReqs.length})
            </span>
            <span className="text-[10px] text-amber-700">— create the PI (or reject) inside the project's Vendor Management</span>
          </div>
          {payReqs.map((r) => (
            <div key={r.id} className="border-b border-amber-100 last:border-b-0" data-testid={`payreq-row-${r.request_number}`}>
              <div className="px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="text-xs font-black text-slate-800">{r.request_number}</span>
              <span className="text-[11px] text-slate-600">{r.project_code}</span>
              <span className="text-[11px] text-slate-600">PO {r.po_number}</span>
              <span className="text-[11px] text-slate-600 truncate max-w-[200px]">{r.vendor_name}</span>
              {r.grn_total != null && <span className="text-[11px] font-bold tabular-nums text-slate-700">₹ {Number(r.grn_total).toLocaleString("en-IN")}</span>}
              <span className="text-[10px] text-slate-400" title={r.requested_by}>by {formatUser(r.requested_by)} · {(r.created_at || "").slice(0, 10)}</span>
              {r.remark && <span className="text-[10px] text-slate-500 italic truncate max-w-[240px]">“{r.remark}”</span>}
              <button
                data-testid={`payreq-view-${r.request_number}`}
                onClick={() => setPayReqView(r)}
                className="ml-auto text-[10px] font-bold uppercase tracking-wider text-amber-800 border border-amber-400 hover:bg-amber-100 px-2.5 py-1 transition-colors">
                View
              </button>
              <button
                data-testid={`payreq-createpi-${r.request_number}`}
                onClick={() => createPiFromPayReq(r)}
                className="text-[10px] font-bold uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1 transition-colors">
                Create PI
              </button>
              <button
                data-testid={`payreq-open-${r.request_number}`}
                onClick={() => openPayReqProject(r)}
                className="text-[10px] font-bold uppercase tracking-wider text-white bg-amber-600 hover:bg-amber-700 px-2.5 py-1 transition-colors">
                Open Project →
              </button>
              <button
                data-testid={`payreq-reject-${r.request_number}`}
                onClick={() => setPayReqReject((p) => p.id === r.id ? { id: "", reason: "", busy: false } : { id: r.id, reason: "", busy: false })}
                className="text-[10px] font-bold uppercase tracking-wider text-rose-700 border border-rose-300 hover:bg-rose-50 px-2.5 py-1 transition-colors">
                Reject
              </button>
              </div>
              {payReqReject.id === r.id && (
                <div className="px-4 pb-2 flex items-center gap-2" data-testid={`payreq-reject-form-${r.request_number}`}>
                  <input
                    data-testid={`payreq-reject-reason-${r.request_number}`}
                    value={payReqReject.reason}
                    onChange={(e) => setPayReqReject((p) => ({ ...p, reason: e.target.value }))}
                    placeholder="Rejection reason (mandatory)…"
                    className="flex-1 border border-rose-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:border-rose-500"
                  />
                  <button
                    data-testid={`payreq-reject-confirm-${r.request_number}`}
                    disabled={payReqReject.busy || !payReqReject.reason.trim()}
                    onClick={async () => {
                      setPayReqReject((p) => ({ ...p, busy: true }));
                      try {
                        await api.post(`/po-payment-requests/${r.id}/reject-assigned`, { reason: payReqReject.reason.trim() });
                        toast.success(`${r.request_number} rejected — the requester has been notified.`);
                        setPayReqs((rows) => rows.filter((x) => x.id !== r.id));
                        setPayReqReject({ id: "", reason: "", busy: false });
                      } catch (ex) {
                        toast.error(ex?.response?.data?.detail || "Could not reject the request");
                        setPayReqReject((p) => ({ ...p, busy: false }));
                      }
                    }}
                    className="text-[10px] font-bold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 px-3 py-1.5 transition-colors">
                    Confirm Reject
                  </button>
                  <button onClick={() => setPayReqReject({ id: "", reason: "", busy: false })}
                    className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-300 hover:bg-slate-100 px-2.5 py-1.5 transition-colors">
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Jun-2026 — payment-request detail modal (View button on assigned rows). */}
      {payReqView && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="payreq-view-modal" onClick={() => setPayReqView(null)}>
          <div className="bg-white w-full max-w-lg shadow-2xl border border-amber-300 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-amber-200 bg-amber-50 flex items-center gap-2">
              <Clock size={14} className="text-amber-700" />
              <span className="text-sm font-black text-slate-800" data-testid="payreq-modal-number">{payReqView.request_number}</span>
              <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-amber-200 text-amber-900">Pending — PI awaited</span>
              <button onClick={() => setPayReqView(null)} data-testid="payreq-modal-close"
                className="ml-auto text-slate-400 hover:text-slate-700 text-lg leading-none px-1">×</button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Project</div>
                <div className="font-semibold text-slate-800" data-testid="payreq-modal-project">
                  {payReqView.project_code}{payReqView.project_name ? ` — ${payReqView.project_name}` : ""}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">PO Number</div>
                <div className="font-semibold text-slate-800" data-testid="payreq-modal-po">{payReqView.po_number || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Vendor</div>
                <div className="font-semibold text-slate-800" data-testid="payreq-modal-vendor">{payReqView.vendor_name || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Requested Amount</div>
                <div className="font-black text-sm tabular-nums text-emerald-700" data-testid="payreq-modal-amount">
                  {payReqView.requested_amount != null ? `₹ ${Number(payReqView.requested_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">GRN Total</div>
                <div className="font-semibold tabular-nums text-slate-700" data-testid="payreq-modal-grn-total">
                  {payReqView.grn_total != null && Number(payReqView.grn_total) > 0
                    ? `₹ ${Number(payReqView.grn_total).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—"}
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">GRNs Covered</div>
                {(payReqView.grn_numbers || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1 mt-0.5" data-testid="payreq-modal-grns">
                    {payReqView.grn_numbers.map((g) => (
                      <span key={g} className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 text-[10px] font-mono text-slate-700">{g}</span>
                    ))}
                  </div>
                ) : (
                  <div className="text-slate-500 italic" data-testid="payreq-modal-grns">No GRN — request raised without GRN</div>
                )}
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Remarks</div>
                <div className="text-slate-700 whitespace-pre-wrap break-words" data-testid="payreq-modal-remark">
                  {payReqView.remark || <span className="text-slate-400 italic">—</span>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Requested By</div>
                <div className="text-slate-700" title={payReqView.requested_by} data-testid="payreq-modal-requested-by">
                  {formatUser(payReqView.requested_by)}
                  <span className="text-slate-400"> · {(payReqView.created_at || "").slice(0, 10)}</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Assigned To</div>
                <div className="text-slate-700" data-testid="payreq-modal-assignees">
                  {(payReqView.assignee_names || []).filter(Boolean).join(", ") || "—"}
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
              <button
                data-testid="payreq-modal-reject"
                onClick={() => {
                  setPayReqReject({ id: payReqView.id, reason: "", busy: false });
                  setPayReqView(null);
                }}
                className="text-[10px] font-bold uppercase tracking-wider text-rose-700 border border-rose-300 hover:bg-rose-50 px-3 py-1.5 transition-colors">
                Reject…
              </button>
              <button
                data-testid="payreq-modal-create-pi"
                onClick={() => { const r = payReqView; setPayReqView(null); createPiFromPayReq(r); }}
                className="ml-auto text-[10px] font-bold uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 transition-colors">
                Create PI →
              </button>
              <button
                data-testid="payreq-modal-open-project"
                onClick={() => { const r = payReqView; setPayReqView(null); openPayReqProject(r); }}
                className="text-[10px] font-bold uppercase tracking-wider text-white bg-amber-600 hover:bg-amber-700 px-3 py-1.5 transition-colors">
                Open Project →
              </button>
              <button onClick={() => setPayReqView(null)}
                className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border border-slate-300 hover:bg-slate-100 px-3 py-1.5 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="border border-slate-200 bg-white" data-testid="approvals-list">
        {/* Compact table header */}
        <div className="hidden md:grid grid-cols-[28px,150px,90px,1fr,110px,160px,250px] gap-2 px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 border-b border-slate-200 bg-slate-50">
          <span></span>
          <span>Voucher</span>
          <span>Status</span>
          <span>Document · Party</span>
          <span className="text-right">Amount</span>
          <span>Raised By · Date</span>
          <span className="text-center">Actions</span>
        </div>

        {filteredList.map(req => {
          const isExpanded = expanded.has(req.id);
          const isBulkable = shouldShowApproveButtons(req);
          const openQ = openQueryFor(req);
          const showRespondForm = shouldShowRespondForm(req);
          const queryCount = (req.queries || []).length;
          const historyCount = (req.history || []).length;
          const hasDetailToShow = queryCount > 0 || historyCount > 0 || showRespondForm || (queryOpenFor === req.id);
          // Jul-2026 — "waiting on whom" label for Pending / On-Hold rows.
          const pendingWith = ["Pending", "Query Raised"].includes(req.status)
            ? (req.pending_with || []).map(u => formatUser(u)).join(", ")
            : "";

          return (
            <div key={req.id} data-approval-row={req.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/30">
              {/* Mobile: stacked card. Desktop (md+): tight table row. */}
              <div className="md:hidden p-3 space-y-2">
                {/* Row 1: checkbox + module + status + amount */}
                <div className="flex items-center gap-2 flex-wrap">
                  {isBulkable ? (
                    <input
                      type="checkbox"
                      data-testid={`approvals-select-${req.id}`}
                      checked={selected.has(req.id)}
                      onChange={() => toggleSelected(req.id)}
                      className="w-4 h-4 accent-[#F97316] shrink-0"
                      title="Select for bulk action"
                    />
                  ) : (
                    <button onClick={() => toggleExpanded(req.id)} className="p-0.5 text-slate-400 hover:text-slate-700 shrink-0" title="Expand">
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                  )}
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-orange-100 text-orange-800 whitespace-nowrap">
                    {MODULE_LABEL[req.module] || req.module}
                  </span>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 whitespace-nowrap ${
                    req.status === "Approved" ? "bg-emerald-100 text-emerald-800" :
                    req.status === "Rejected" ? "bg-rose-100 text-rose-800" :
                    req.status === "Query Raised" ? "bg-amber-100 text-amber-800" :
                    "bg-blue-100 text-blue-800"
                  }`}>
                    {req.status === "Pending" ? `L${req.current_level}/L${req.final_level}` :
                      req.status === "Query Raised" ? "On Hold" : req.status}
                  </span>
                  {req.via_role_name && (
                    <span data-testid={`approvals-via-role-m-${req.id}`} className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-sky-100 text-sky-800 whitespace-nowrap" title={`You can act on this because your role is ${req.via_role_name}`}>
                      via {req.via_role_name}
                    </span>
                  )}
                  {typeof req.document_total_amount === "number" && (
                    <span data-testid={`approvals-amount-${req.id}`} className="ml-auto text-sm font-bold text-slate-900 tabular-nums">
                      ₹ {new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(req.document_total_amount)}
                    </span>
                  )}
                </div>

                {/* Row 2: document # + party */}
                <button onClick={() => toggleExpanded(req.id)} className="text-left w-full" data-testid={`approvals-row-toggle-m-${req.id}`}>
                  <div className="text-sm font-bold text-slate-900 break-words flex items-center gap-1">
                    {hasDetailToShow && (isExpanded ? <ChevronDown size={12} className="text-slate-400 shrink-0" /> : <ChevronRight size={12} className="text-slate-400 shrink-0" />)}
                    {req.document_number || req.document_id}
                  </div>
                  {req.document_party_name && <div className="text-xs text-slate-500 break-words">{req.document_party_name}</div>}
                </button>

                {/* Row 3: raised by + date */}
                <div className="text-[11px] text-slate-500 break-all" title={req.raised_by_email}>
                  {formatUser(req.raised_by_email)}
                  <span className="text-[10px] text-slate-400 ml-2">
                    <Clock size={9} className="inline" /> {new Date(req.created_at).toLocaleDateString()} {new Date(req.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>

                {/* Row 4: actions — full-width buttons */}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    data-testid={`view-doc-m-${req.id}`}
                    onClick={() => viewDocument(req)}
                    disabled={viewBusy === req.id}
                    className="flex-1 min-w-[80px] inline-flex items-center justify-center gap-1 border border-slate-300 hover:border-[#F97316] hover:text-[#F97316] text-slate-600 text-xs font-bold uppercase tracking-wider py-2 disabled:opacity-60">
                    {viewBusy === req.id
                      ? <RefreshCw size={12} className="animate-spin" />
                      : <Eye size={12} />} View
                  </button>
                  {isBulkable && (
                    <>
                      <button
                        data-testid={`raise-query-m-${req.id}`}
                        onClick={() => { setQueryOpenFor(queryOpenFor === req.id ? null : req.id); setExpanded((p) => { const n = new Set(p); n.add(req.id); return n; }); }}
                        className="flex-1 min-w-[80px] inline-flex items-center justify-center gap-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold uppercase tracking-wider py-2">
                        <HelpCircle size={12} /> Query
                      </button>
                      <button
                        data-testid={`approve-m-${req.id}`}
                        onClick={() => decide(req.id, "approve")}
                        disabled={deciding.has(req.id)}
                        className="flex-1 min-w-[80px] inline-flex items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider py-2 disabled:opacity-50">
                        {deciding.has(req.id) ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
                      </button>
                      <button
                        data-testid={`reject-m-${req.id}`}
                        onClick={() => decide(req.id, "reject")}
                        disabled={deciding.has(req.id)}
                        className="flex-1 min-w-[80px] inline-flex items-center justify-center gap-1 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase tracking-wider py-2 disabled:opacity-50">
                        <XCircle size={12} /> Reject
                      </button>
                      <input
                        data-testid={`approval-comment-m-${req.id}`}
                        value={comment[req.id] || ""}
                        onChange={e => setComment(c => ({ ...c, [req.id]: e.target.value }))}
                        placeholder="Optional remark…"
                        className="w-full text-xs px-2 py-2 border border-slate-300 focus:border-[#F97316] focus:outline-none"
                      />
                    </>
                  )}
                  {!isBulkable && req.status === "Query Raised" && (
                    <span className="text-[10px] text-amber-700 font-bold">
                      Awaiting reply from {formatUser(openQ?.raised_to_email)}
                    </span>
                  )}
                </div>
              </div>

              {/* Desktop: compact single-row grid layout (md and up) */}
              <div className="hidden md:grid grid-cols-[28px,150px,90px,1fr,110px,160px,250px] gap-2 px-3 py-2 items-center">
                {/* Checkbox / expand toggle */}
                <div className="flex items-center justify-center">
                  {isBulkable ? (
                    <input
                      type="checkbox"
                      data-testid={`approvals-select-${req.id}`}
                      checked={selected.has(req.id)}
                      onChange={() => toggleSelected(req.id)}
                      className="w-4 h-4 accent-[#F97316]"
                      title="Select for bulk action"
                    />
                  ) : (
                    <button onClick={() => toggleExpanded(req.id)} className="p-0.5 text-slate-400 hover:text-slate-700" title="Expand">
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                  )}
                </div>

                {/* Module pill — wider column now (150px) + no truncation
                    so the full voucher type ("Purchase Order", "Stock
                    Adjustment Voucher", etc.) renders end-to-end. Long
                    labels wrap to a second line rather than getting
                    cut off with an ellipsis. */}
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-orange-100 text-orange-800 text-center leading-tight self-center">
                  {MODULE_LABEL[req.module] || req.module}
                </span>

                {/* Status pill + waiting-on-whom */}
                <div className="flex flex-col items-center gap-0.5 self-center min-w-0">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 text-center whitespace-nowrap ${
                    req.status === "Approved" ? "bg-emerald-100 text-emerald-800" :
                    req.status === "Rejected" ? "bg-rose-100 text-rose-800" :
                    req.status === "Query Raised" ? "bg-amber-100 text-amber-800" :
                    "bg-blue-100 text-blue-800"
                  }`} title={req.status}>
                    {req.status === "Pending" ? `L${req.current_level}/L${req.final_level}` :
                      req.status === "Query Raised" ? "On Hold" : req.status}
                  </span>
                  {pendingWith && (
                    <span className="text-[9px] text-slate-500 leading-tight text-center max-w-[110px] truncate" title={`Waiting on ${pendingWith}`}
                      data-testid={`approvals-pending-with-${req.id}`}>
                      with <b className="text-slate-700">{pendingWith}</b>
                    </span>
                  )}
                  {req.via_role_name && (
                    <span data-testid={`approvals-via-role-${req.id}`} className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-sky-100 text-sky-800 whitespace-nowrap" title={`You can act on this because your role is ${req.via_role_name}`}>
                      via {req.via_role_name}
                    </span>
                  )}
                </div>

                {/* Document number + party */}
                <div className="min-w-0">
                  <button
                    onClick={() => toggleExpanded(req.id)}
                    className="text-left w-full"
                    data-testid={`approvals-row-toggle-${req.id}`}
                  >
                    <div className="text-xs font-bold text-slate-900 truncate flex items-center gap-1">
                      {hasDetailToShow && (
                        isExpanded ? <ChevronDown size={11} className="text-slate-400 shrink-0" /> : <ChevronRight size={11} className="text-slate-400 shrink-0" />
                      )}
                      {req.document_number || req.document_id}
                    </div>
                    {req.document_party_name && (
                      <div className="text-[11px] text-slate-500 truncate">{req.document_party_name}</div>
                    )}
                  </button>
                </div>

                {/* Amount */}
                <div className="text-right tabular-nums">
                  {typeof req.document_total_amount === "number" ? (
                    <span data-testid={`approvals-amount-${req.id}`} className="text-xs font-bold text-slate-900">
                      ₹ {new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(req.document_total_amount)}
                    </span>
                  ) : <span className="text-[11px] text-slate-300">—</span>}
                </div>

                {/* Raised by + date */}
                <div className="text-[11px] text-slate-500 min-w-0">
                  <div className="truncate" title={req.raised_by_email}>{formatUser(req.raised_by_email)}</div>
                  <div className="text-[10px] text-slate-400 flex items-center gap-1">
                    <Clock size={9} /> {new Date(req.created_at).toLocaleDateString()} {new Date(req.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>

                {/* Actions — icon-only buttons */}
                <div className="flex items-center justify-end gap-1">
                  <button
                    data-testid={`view-doc-${req.id}`}
                    onClick={() => viewDocument(req)}
                    disabled={viewBusy === req.id}
                    title="View document"
                    className="w-7 h-7 inline-flex items-center justify-center border border-slate-300 hover:border-[#F97316] hover:text-[#F97316] text-slate-600 disabled:opacity-60">
                    {viewBusy === req.id
                      ? <RefreshCw size={13} className="animate-spin" />
                      : <Eye size={13} />}
                  </button>
                  {isBulkable && (
                    <>
                      <button
                        data-testid={`raise-query-${req.id}`}
                        onClick={() => { setQueryOpenFor(queryOpenFor === req.id ? null : req.id); setExpanded((p) => { const n = new Set(p); n.add(req.id); return n; }); }}
                        title="Raise clarification query"
                        className="w-7 h-7 inline-flex items-center justify-center bg-amber-500 hover:bg-amber-600 text-white">
                        <HelpCircle size={13} />
                      </button>
                      <button
                        data-testid={`approve-${req.id}`}
                        onClick={() => decide(req.id, "approve")}
                        disabled={deciding.has(req.id)}
                        title="Approve"
                        className="w-7 h-7 inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
                        {deciding.has(req.id) ? <RefreshCw size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                      </button>
                      <button
                        data-testid={`reject-${req.id}`}
                        onClick={() => decide(req.id, "reject")}
                        disabled={deciding.has(req.id)}
                        title="Reject"
                        className="w-7 h-7 inline-flex items-center justify-center bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50">
                        <XCircle size={13} />
                      </button>
                      <input
                        data-testid={`approval-comment-${req.id}`}
                        value={comment[req.id] || ""}
                        onChange={e => setComment(c => ({ ...c, [req.id]: e.target.value }))}
                        placeholder="Remark…"
                        className="hidden xl:block w-[110px] text-[11px] px-2 py-1 border border-slate-300 focus:border-[#F97316] focus:outline-none"
                      />
                    </>
                  )}
                  {!isBulkable && req.status === "Query Raised" && (
                    <span className="text-[10px] text-amber-700 font-bold whitespace-nowrap" title={`Awaiting ${openQ?.raised_to_email || "—"}`}>
                      Awaiting reply
                    </span>
                  )}
                  {!isBulkable && req.status === "Pending" && req.kind === "upcoming" && (
                    <span className="text-[10px] text-slate-600 font-bold whitespace-nowrap inline-flex items-center gap-1"
                          data-testid={`upcoming-waiting-${req.id}`}
                          title="Waiting for the lower-level approver to act. Once they approve, this voucher will move to your Immediate queue.">
                      <Clock size={11} className="text-amber-600" /> Awaiting L{req.current_level}
                    </span>
                  )}
                </div>
              </div>

              {/* Expanded detail panel — only renders when needed */}
              {(isExpanded || queryOpenFor === req.id || showRespondForm) && (
                <div className="px-3 pb-3 bg-slate-50/40">
                  {queryOpenFor === req.id && req.status === "Pending" && (
                    <QueryRaiseInline
                      requestId={req.id}
                      onRaised={() => { setQueryOpenFor(null); load(); }}
                      onClose={() => setQueryOpenFor(null)}
                    />
                  )}
                  {showRespondForm && (
                    <QueryRespondInline
                      requestId={req.id}
                      query={openQ}
                      onResponded={load}
                    />
                  )}
                  {isExpanded && (
                    <>
                      <QueryHistory queries={req.queries || []} />
                      {historyCount > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Audit Trail · {historyCount}</div>
                          <div className="space-y-0.5">
                            {req.history.map((h, i) => (
                              <div key={i} className="text-[11px] text-slate-600 flex flex-wrap items-center gap-x-2">
                                <span className="font-bold uppercase text-slate-700">{h.action}</span>
                                <span>· L{h.level || 0}</span>
                                <span className="break-all" title={h.user_email}>· {formatUser(h.user_email)}</span>
                                <span className="text-slate-400">· {new Date(h.at).toLocaleString()}</span>
                                {h.comment && <span className="italic text-slate-500 break-words">"{h.comment}"</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sprint 2026-Feb-13 — Estimate Approval enhancement dialog */}
      {approveEstFor && (
        <ApproveEstimateDialog
          request={approveEstFor.request}
          estimate={approveEstFor.estimate}
          onClose={() => setApproveEstFor(null)}
          onDone={() => { setApproveEstFor(null); load(); }}
        />
      )}

      {/* Document viewer modal */}
      {viewing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="view-doc-modal">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-orange-700 font-bold">
                  {MODULE_LABEL[viewing.request.module]} · {viewing.orphan ? "Source Deleted" : (viewing.doc?.status || viewing.doc?.approval_status || "Pending")}
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {viewing.request.module === "vendor_master"
                    ? `${viewing.doc?.vendor_code || ""} · ${viewing.doc?.name || viewing.request.document_id}`
                    : (viewing.doc?.estimate_number || viewing.doc?.invoice_number || viewing.doc?.voucher_number || viewing.request.document_number || viewing.request.document_id)}
                </div>
              </div>
              <button onClick={() => setViewing(null)} className="p-1 hover:bg-slate-100" data-testid="close-view-doc">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3 text-xs">
              {viewing.orphan ? (
                <div className="border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900" data-testid="approval-doc-orphan">
                  <div className="font-bold uppercase tracking-wider text-[11px] mb-1">Source document no longer exists</div>
                  <div className="text-[11px] leading-relaxed">
                    The underlying {MODULE_LABEL[viewing.request.module]} <strong>{viewing.request.document_number || viewing.request.document_id}</strong> was deleted after this approval was recorded.
                    The audit trail below is preserved so you can see who actioned the request and when. The document itself cannot be opened.
                  </div>
                  {viewing.request.document_total_amount != null && (
                    <div className="mt-2 text-[11px]">Last recorded amount: <strong className="tabular">₹ {Number(viewing.request.document_total_amount).toLocaleString("en-IN")}</strong></div>
                  )}
                  {viewing.request.document_party_name && (
                    <div className="text-[11px]">Party: <strong>{viewing.request.document_party_name}</strong></div>
                  )}
                </div>
              ) : viewing.request.module === "vendor_master" ? (
                <VendorSummary doc={viewing.doc} />
              ) : (
                <DocSummary doc={viewing.doc} />
              )}
              {!viewing.orphan && viewing.request.module === "purchase_order" && (
                <BoqPoDeductionNote po={viewing.doc} />
              )}
              {!viewing.orphan && viewing.opsPay && (() => {
                const t = viewing.opsPay.totals || {};
                const o = viewing.opsPay.other_totals || {};
                const fmt = (v) => (v == null ? "—" : `₹ ${Number(v).toLocaleString("en-IN")}`);
                const box = (label, v, cls = "text-slate-900") => (
                  <div key={label} className="flex-1 min-w-[120px] border border-slate-200 rounded bg-white px-2.5 py-1.5">
                    <div className="text-[8px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
                    <div className={`text-[12px] font-bold tabular-nums ${cls}`}>{v}</div>
                  </div>
                );
                return (
                  <div className="border border-emerald-200 bg-emerald-50/40 rounded p-2.5 space-y-1.5" data-testid="approval-ops-pay-summary">
                    <div className="text-[9px] uppercase tracking-wider font-bold text-emerald-800">
                      Ops Project — PO Payment Summary · {viewing.opsPay.project_code || viewing.opsPay.project_name}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {box("Purchase Orders", t.po_count ?? 0)}
                      {box("PO Value (inc. GST)", fmt(t.po_total_inc_gst))}
                      {box("Invoiced (Net Payable)", fmt(t.invoiced_net_payable))}
                      {box("Paid", fmt(t.paid), "text-emerald-700")}
                      {box("Balance Payable", fmt(t.balance), "text-rose-700")}
                      {(o.invoice_count || 0) > 0 && box("Other PrimeWork Paid", fmt(o.paid), "text-emerald-700")}
                      {box("Net Total Paid", fmt(viewing.opsPay.net_total_paid), "text-emerald-800")}
                      {(Number(viewing.opsPay.opening_expenditure) || 0) > 0 && (
                        <>
                          {box("Opening Expenditure (Prev. ERP)", fmt(viewing.opsPay.opening_expenditure), "text-indigo-700")}
                          {box("Grand Total (incl. Opening)", fmt(viewing.opsPay.grand_total_incl_opening), "text-indigo-900")}
                        </>
                      )}
                    </div>
                    {!viewing.opsPay.commercials_visible && (
                      <div className="text-[9px] text-amber-700">₹ values hidden — you do not hold the PR Commercials permission.</div>
                    )}
                  </div>
                );
              })()}
              {!viewing.orphan && viewing.projCtx && (() => {
                const c = viewing.projCtx;
                const fmt = (v) => (v == null ? "—" : `₹ ${Number(v).toLocaleString("en-IN")}`);
                const box = (label, v, cls = "text-slate-900") => (
                  <div key={label} className="flex-1 min-w-[120px] border border-slate-200 rounded bg-white px-2.5 py-1.5">
                    <div className="text-[8px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
                    <div className={`text-[12px] font-bold tabular-nums ${cls}`}>{v}</div>
                  </div>
                );
                return (
                  <div className="border border-violet-200 bg-violet-50/40 rounded p-2.5 space-y-1.5" data-testid="approval-project-budget-context">
                    <div className="text-[9px] uppercase tracking-wider font-bold text-violet-800 flex items-center gap-2">
                      Project Budget Context · {c.project_code || c.project_name}
                      {c.over_budget && (
                        <span className="text-[9px] uppercase font-bold text-rose-700 bg-rose-50 border border-rose-300 px-1.5 py-0.5">Over Budget</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {box("Budget", c.budgeted_amount > 0 ? fmt(c.budgeted_amount) : "Not set")}
                      {box("Total Expenditure", fmt(c.total_expenditure), c.over_budget ? "text-rose-700" : "text-slate-900")}
                      {c.budget_balance != null && box("Budget Balance", fmt(c.budget_balance), c.budget_balance < 0 ? "text-rose-700" : "text-emerald-700")}
                      {box("Total Sales", fmt(c.total_sales), "text-emerald-700")}
                      {box("Direct Margin", fmt(c.direct_margin), c.direct_margin < 0 ? "text-rose-700" : "text-slate-900")}
                    </div>
                  </div>
                );
              })()}
              {!viewing.orphan && viewing.request.module !== "vendor_master" && (viewing.doc.line_items || []).length > 0 && (() => {
                // Issue Vouchers / Material vouchers don't carry GST + use
                // different field names than invoices/bills:
                //   • rate    → unit_rate
                //   • amount  → line_value
                //   • no GST column
                const mod = viewing.request.module || "";
                // Jun-2026 (user request) — Stock Adjustment approvers must
                // see book qty, adjustment ±, resulting qty and ₹ value per
                // line before approving. SAV lines use their own field names
                // (before_qty / adjustment_qty / after_qty / unit_rate), so
                // render a dedicated table instead of the generic one.
                if (mod === "stock_adjustment_voucher") {
                  const d = viewing.doc || {};
                  const lines = d.line_items || [];
                  const totalVal = lines.reduce((s, l) => s + Number(l.adjustment_qty || 0) * Number(l.unit_rate || 0), 0);
                  const fmtQ = (v) => Number(v ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 4 });
                  const fmtV = (v) => `${v < 0 ? "-" : ""}₹ ${Math.abs(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  const chip = (label, val, cls = "bg-slate-50 text-slate-700 border-slate-200") => val ? (
                    <span key={label} className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] ${cls}`}>
                      <span className="uppercase tracking-wider font-bold text-[8px] opacity-60">{label}</span> {val}
                    </span>
                  ) : null;
                  return (
                    <div className="space-y-2" data-testid="sav-approval-detail">
                      <div className="flex flex-wrap gap-1.5">
                        {chip("Warehouse", d.warehouse_name)}
                        {chip("Type", d.adjustment_type, "bg-sky-50 text-sky-800 border-sky-200")}
                        {chip("Date", d.adjustment_date)}
                        {chip("Reason", d.reason, "bg-amber-50 text-amber-800 border-amber-200")}
                        {chip("Reference", d.reference_document)}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-slate-200">
                          <thead className="bg-slate-50 border-b-2 border-slate-200">
                            <tr>
                              {["#", "Item", "Batch", "Book Qty", "Adjustment ±", "Qty After", "Unit", "Rate", "Value (₹)", "Line Remarks"].map(h =>
                                <th key={h} className={`py-2 px-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 ${["Book Qty", "Adjustment ±", "Qty After", "Rate", "Value (₹)"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {lines.map((l, i) => {
                              const adj = Number(l.adjustment_qty || 0);
                              const val = adj * Number(l.unit_rate || 0);
                              return (
                                <tr key={i} className="border-b border-slate-100" data-testid={`sav-approval-line-${i}`}>
                                  <td className="py-1.5 px-2 text-slate-500">{i + 1}</td>
                                  <td className="py-1.5 px-2 text-slate-800">
                                    <span className="font-mono text-[11px]">{l.item_code || "—"}</span>
                                    <div className="text-[10px] text-slate-500">{l.item_name}</div>
                                  </td>
                                  <td className="py-1.5 px-2 font-mono text-[10px] text-slate-500">{l.batch_code || <span className="italic">new batch</span>}</td>
                                  <td className="py-1.5 px-2 text-right tabular text-slate-700" data-testid={`sav-book-qty-${i}`}>{fmtQ(l.before_qty)}</td>
                                  <td className={`py-1.5 px-2 text-right tabular font-bold ${adj < 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid={`sav-adj-qty-${i}`}>
                                    {adj > 0 ? "+" : ""}{fmtQ(adj)}
                                  </td>
                                  <td className="py-1.5 px-2 text-right tabular font-semibold text-slate-800" data-testid={`sav-after-qty-${i}`}>{fmtQ(l.after_qty)}</td>
                                  <td className="py-1.5 px-2 text-slate-500">{l.unit || ""}</td>
                                  <td className="py-1.5 px-2 text-right tabular text-slate-700">{Number(l.unit_rate || 0).toFixed(2)}</td>
                                  <td className={`py-1.5 px-2 text-right tabular font-semibold ${val < 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid={`sav-value-${i}`}>{fmtV(val)}</td>
                                  <td className="py-1.5 px-2 text-[10px] text-slate-500">{l.remarks || "—"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="bg-slate-50 border-t-2 border-slate-200">
                              <td colSpan={8} className="py-2 px-2 text-right text-[10px] uppercase tracking-wider font-bold text-slate-500">Net Adjustment Value</td>
                              <td className={`py-2 px-2 text-right tabular font-black ${totalVal < 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="sav-total-value">{fmtV(totalVal)}</td>
                              <td />
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      {d.remarks && <div className="text-[11px] text-slate-500 italic">Voucher remarks: {d.remarks}</div>}
                    </div>
                  );
                }
                const isIV = mod === "issue_voucher" || mod === "material_voucher" || mod === "return_voucher";
                // Jul-2026 — approvers on Issue Vouchers must see each line's
                // Issue Type + expected return date before approving.
                const showIssueType = mod === "issue_voucher";
                // Aug-2026 (user request) — Return Voucher approvers must see
                // each line's Asset/Inventory type + condition on return.
                const showCondition = mod === "return_voucher";
                // Jun-2026 (user request) — PO approvers (standard + Ops) must
                // see each line's Item Type (Inventory / Service / Asset).
                const showItemType = mod === "purchase_order";
                // Jun-2026 (user request) — Project PR line-level attachments
                // must be visible + openable in the approval view.
                const isPrMod = mod === "project_purchase_requisition";
                const showPrDoc = isPrMod && (viewing.doc.line_items || []).some(l => l.attachment);
                const headers = isIV
                  ? ["#", "Item / Asset",
                     ...(showCondition ? ["Type"] : []),
                     "Description", "Qty", "Unit", "Rate", "Amount",
                     ...(showIssueType ? ["Issue Type", "Return Expected"] : []),
                     ...(showCondition ? ["Issue Date", "Condition"] : [])]
                  : ["#", "Item Code",
                     ...(showItemType ? ["Item Type"] : []),
                     "Description", "Qty", "Unit", "Rate", "GST %", "Amount",
                     ...(showPrDoc ? ["Doc"] : [])];
                const fmtRet = (d) => {
                  if (!d) return "—";
                  try {
                    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
                  } catch { return d; }
                };
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-slate-200">
                      <thead className="bg-slate-50 border-b-2 border-slate-200">
                        <tr>
                          {headers.map(h =>
                            <th key={h} className="text-left py-2 px-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">{h}</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {(viewing.doc.line_items || []).map((l, i) => {
                          // Project PR lines carry estimated_rate / tax_percent /
                          // total_value (stripped server-side without the
                          // pr_commercials permission) — fall through to them.
                          const rate   = Number(l.unit_rate ?? l.rate ?? l.estimated_rate ?? 0);
                          const qty    = Number(l.return_qty ?? l.quantity ?? (l.line_type === "Asset" || l.asset_id ? 1 : 0));
                          const amount = Number(l.line_value ?? l.amount ?? l.total_value ?? (qty * rate));
                          // Server strips commercial fields for viewers without
                          // pr_commercials — show "—" rather than a false 0.00.
                          const noCommercials = (l.unit_rate ?? l.rate ?? l.estimated_rate) == null;
                          const isAssetLine = !!l.asset_id || l.line_type === "Asset";
                          const cond = l.condition_on_return || "";
                          const condCls = cond === "Good" ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                            : cond === "Lost" ? "bg-rose-50 text-rose-700 border-rose-300"
                            : cond ? "bg-amber-50 text-amber-800 border-amber-300"
                            : "";
                          const itemLbl = l.asset_code
                            ? `${l.asset_code} · ${l.item_name || ""}`
                            : (l.item_code || l.item_name || "—");
                          const descr = l.description || l.item_name || (l.batch_code ? `Batch ${l.batch_code}` : "");
                          return (
                            <tr key={i} className="border-b border-slate-100">
                              <td className="py-1.5 px-2 text-slate-500">{i + 1}</td>
                              <td className="py-1.5 px-2 text-slate-800">
                                <span className="font-mono text-[11px]">{itemLbl}</span>
                                {l.warehouse_name && (
                                  <div className="text-[10px] text-slate-400 mt-0.5">@ {l.warehouse_name}</div>
                                )}
                              </td>
                              {showCondition && (
                                <td className="py-1.5 px-2 whitespace-nowrap" data-testid={`rv-approval-type-${i}`}>
                                  <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                                    isAssetLine ? "bg-slate-100 text-slate-600 border-slate-300" : "bg-sky-50 text-sky-700 border-sky-300"
                                  }`}>{isAssetLine ? "Asset" : "Inventory"}</span>
                                </td>
                              )}
                              {showItemType && (() => {
                                const t = (l.item_type || "").trim();
                                const tl = t.toLowerCase();
                                const cls = tl === "inventory" ? "bg-sky-50 text-sky-700 border-sky-300"
                                  : tl === "service" ? "bg-violet-50 text-violet-700 border-violet-300"
                                  : tl === "asset" ? "bg-slate-100 text-slate-600 border-slate-300"
                                  : "bg-slate-50 text-slate-500 border-slate-200";
                                return (
                                  <td className="py-1.5 px-2 whitespace-nowrap" data-testid={`po-approval-item-type-${i}`}>
                                    {t ? (
                                      <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>{t}</span>
                                    ) : "—"}
                                  </td>
                                );
                              })()}
                              <td className="py-1.5 px-2 text-slate-700">{descr || "—"}</td>
                              <td className="py-1.5 px-2 text-right tabular text-slate-700">{qty}</td>
                              <td className="py-1.5 px-2 text-slate-500">{l.unit || (isAssetLine ? "Asset" : "")}</td>
                              <td className="py-1.5 px-2 text-right tabular text-slate-700">{noCommercials ? "—" : rate.toFixed(2)}</td>
                              {!isIV && (
                                <td className="py-1.5 px-2 text-right tabular text-slate-500">{l.gst_rate ?? l.tax_percent ?? 0}%</td>
                              )}
                              <td className="py-1.5 px-2 text-right tabular font-semibold text-slate-800">{noCommercials ? "—" : amount.toFixed(2)}</td>
                              {showPrDoc && (
                                <td className="py-1.5 px-2 whitespace-nowrap" data-testid={`pr-approval-line-doc-${i}`}>
                                  {l.attachment ? (
                                    <button type="button"
                                      onClick={() => blobOpen(`/projects/${viewing.doc.project_id}/prs/${viewing.doc.id}/lines/${l.line_uid}/attachment`, l.attachment.filename)}
                                      className="text-sky-700 hover:underline inline-flex items-center gap-1 text-[11px] font-bold max-w-[180px]"
                                      title={`${l.attachment.filename} (${Math.max(1, Math.round((l.attachment.size || 0) / 1024))} KB)`}>
                                      📎 <span className="truncate">{l.attachment.filename}</span>
                                    </button>
                                  ) : <span className="text-slate-300">—</span>}
                                </td>
                              )}
                              {showIssueType && (
                                <>
                                  <td className="py-1.5 px-2 whitespace-nowrap" data-testid={`iv-approval-issue-type-${i}`}>
                                    {l.issue_type ? (
                                      <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                                        l.issue_type === "Returnable"
                                          ? "bg-amber-50 text-amber-800 border-amber-300"
                                          : "bg-slate-100 text-slate-600 border-slate-300"
                                      }`}>{l.issue_type}</span>
                                    ) : "—"}
                                  </td>
                                  <td className="py-1.5 px-2 whitespace-nowrap text-slate-700" data-testid={`iv-approval-return-date-${i}`}>
                                    {l.issue_type === "Returnable" ? fmtRet(l.expected_return_date) : "—"}
                                  </td>
                                </>
                              )}
                              {showCondition && (
                                <>
                                  <td className="py-1.5 px-2 whitespace-nowrap text-slate-700" data-testid={`rv-approval-issue-date-${i}`}>
                                    {fmtRet(l.issue_date)}
                                    {l.issue_voucher_number && (
                                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">{l.issue_voucher_number}</div>
                                    )}
                                  </td>
                                  <td className="py-1.5 px-2 whitespace-nowrap" data-testid={`rv-approval-condition-${i}`}>
                                    {cond ? (
                                      <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${condCls}`}>{cond}</span>
                                    ) : "—"}
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* ── Sprint 2026-Feb-10f — Bill Payment Voucher detail block ──
                  Approvers need to see per-line PI/source-voucher context,
                  any retention / hold breakdown, and the operator's remark
                  BEFORE deciding. The generic `line_items` block above
                  never renders for BPVs (they use `lines`, not `line_items`),
                  which historically left approvers reviewing a bare totals
                  card. This block surfaces:
                    • PI No / Source Voucher No (per line)
                    • Vendor ref, project, branch
                    • Invoice / GST / TDS breakdown
                    • Retention & Hold breakdown when present
                    • Operator remark / notes
                    • Actual beneficiary (when payment is routed via a
                      factor / authorised collector / sister concern)
              */}
              {!viewing.orphan
                && viewing.request.module === "bill_payment_voucher"
                && (viewing.doc.lines || []).length > 0 && (
                <div className="border border-slate-200" data-testid="bpv-approval-lines">
                  <div className="px-2 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Bills in this Voucher · {viewing.doc.lines.length} line{viewing.doc.lines.length === 1 ? "" : "s"}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead className="bg-white border-b border-slate-200">
                        <tr>
                          {["#", "PI / Source No", "Ref / Project", "Invoice", "GST", "− TDS", "− Retention / Deductions", "− Holds", "Net Payable"].map(h => (
                            <th key={h} className="text-left py-2 px-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {viewing.doc.lines.map((ln, i) => {
                          const deds  = ln.deductions || [];
                          const hlds  = ln.holds || [];
                          // "Retention" is captured as either a deduction
                          // (type contains "Retention") or a hold. Split
                          // them so the approver sees the label + amount
                          // + remarks explicitly per row.
                          const dedTotal = deds.reduce((s, d) => s + Number(d.amount || 0), 0);
                          const hldTotal = hlds.reduce((s, h) => s + (h.released ? 0 : Number(h.amount || 0)), 0);
                          return (
                            <tr key={ln.id || i} className="border-b border-slate-100 align-top"
                                data-testid={`bpv-approval-line-${i}`}>
                              <td className="py-1.5 px-2 text-slate-500 tabular">{i + 1}</td>
                              <td className="py-1.5 px-2">
                                <div className="font-mono text-[11px] font-bold text-slate-800" data-testid={`bpv-approval-source-number-${i}`}>
                                  {ln.source_number || "—"}
                                </div>
                                {ln.source_date && (
                                  <div className="text-[10px] text-slate-400">{ln.source_date}</div>
                                )}
                                {ln.source_module && (
                                  <div className="text-[10px] text-slate-400 italic">
                                    {ln.source_module === "purchase_invoice" ? "Purchase Invoice"
                                      : ln.source_module === "general_payment" ? "General Payment"
                                      : ln.source_module === "salary_payment" ? "Salary Payment"
                                      : ln.source_module === "store_rent" ? "Store Rent"
                                      : ln.source_module}
                                  </div>
                                )}
                              </td>
                              <td className="py-1.5 px-2 text-[11px] text-slate-700">
                                {ln.vendor_ref_no && (
                                  <div>Ref: <span className="font-mono">{ln.vendor_ref_no}</span></div>
                                )}
                                {ln.project_name && <div className="text-slate-500">{ln.project_name}</div>}
                                {ln.branch_name   && <div className="text-slate-400 text-[10px]">@ {ln.branch_name}</div>}
                              </td>
                              <td className="py-1.5 px-2 text-right tabular font-semibold text-slate-800">
                                {Number(ln.invoice_amount || 0).toFixed(2)}
                              </td>
                              <td className="py-1.5 px-2 text-right tabular text-slate-700">
                                {Number(ln.gst_amount || 0).toFixed(2)}
                              </td>
                              <td className="py-1.5 px-2 text-right tabular text-rose-600">
                                {Number(ln.tds_amount || 0) > 0 ? Number(ln.tds_amount).toFixed(2) : "—"}
                              </td>
                              <td className="py-1.5 px-2 text-[11px]" data-testid={`bpv-approval-deductions-${i}`}>
                                {deds.length === 0 ? (
                                  <span className="text-slate-400">—</span>
                                ) : (
                                  <div className="space-y-0.5">
                                    {deds.map((d, di) => (
                                      <div key={d.id || di} className="flex items-baseline gap-1.5">
                                        <span className={`inline-block text-[9px] uppercase tracking-wider px-1 py-px border ${
                                          (d.type || "").toLowerCase().includes("retention")
                                            ? "border-amber-400 text-amber-700 bg-amber-50"
                                            : "border-slate-300 text-slate-600 bg-slate-50"
                                        }`}>{d.type || "Deduction"}</span>
                                        <span className="tabular text-rose-600 font-semibold">{Number(d.amount || 0).toFixed(2)}</span>
                                        {d.remarks && (
                                          <span className="text-[10px] text-slate-500 italic truncate max-w-[160px]" title={d.remarks}>
                                            · {d.remarks}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                    <div className="pt-0.5 border-t border-slate-100 text-right tabular text-rose-600 font-bold">
                                      Σ {dedTotal.toFixed(2)}
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className="py-1.5 px-2 text-[11px]" data-testid={`bpv-approval-holds-${i}`}>
                                {hlds.length === 0 ? (
                                  <span className="text-slate-400">—</span>
                                ) : (
                                  <div className="space-y-0.5">
                                    {hlds.map((h, hi) => (
                                      <div key={h.id || hi} className="flex items-baseline gap-1.5">
                                        <span className={`inline-block text-[9px] uppercase tracking-wider px-1 py-px border ${
                                          (h.type || "").toLowerCase().includes("retention")
                                            ? "border-amber-400 text-amber-700 bg-amber-50"
                                            : "border-slate-300 text-slate-600 bg-slate-50"
                                        }`}>{h.type || "Hold"}</span>
                                        <span className={`tabular font-semibold ${h.released ? "line-through text-slate-400" : "text-amber-700"}`}>{Number(h.amount || 0).toFixed(2)}</span>
                                        {h.released && (
                                          <span className="text-[9px] text-emerald-600 uppercase tracking-wider">released</span>
                                        )}
                                        {h.remarks && (
                                          <span className="text-[10px] text-slate-500 italic truncate max-w-[160px]" title={h.remarks}>
                                            · {h.remarks}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                    <div className="pt-0.5 border-t border-slate-100 text-right tabular text-amber-700 font-bold">
                                      Σ {hldTotal.toFixed(2)}
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className="py-1.5 px-2 text-right tabular font-bold text-slate-900">
                                {Number(ln.net_payable || 0).toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Jun-2026 — Labour Payment Request approval detail block ──
                  Requests carry `rows` (one per attendance day per labourer),
                  NOT `line_items`, so the generic table never renders.
                  Approvers must see every day / labourer / category / rate /
                  amount before deciding. */}
              {!viewing.orphan
                && viewing.request.module === "labour_payment"
                && (viewing.doc.rows || []).length > 0 && (
                <div className="border border-slate-200" data-testid="lp-approval-rows">
                  <div className="px-2 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Labour Payment Request · {viewing.doc.rows.length} day-line{viewing.doc.rows.length === 1 ? "" : "s"}
                    {viewing.doc.project_code ? ` · ${viewing.doc.project_code} ${viewing.doc.project_name || ""}` : ""}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead className="bg-white border-b border-slate-200">
                        <tr>
                          {["#", "Date", "Labour", "Category 1", "Category 2", "Attendance", "OT Hrs", "Units", "Per-Day Rate", "Amount"].map(h => (
                            <th key={h} className="text-left py-2 px-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {viewing.doc.rows.map((r, i) => (
                          <tr key={r.row_key || i} className="border-b border-slate-100 align-top" data-testid={`lp-approval-row-${i}`}>
                            <td className="py-1.5 px-2 text-slate-500 tabular">{i + 1}</td>
                            <td className="py-1.5 px-2 whitespace-nowrap text-slate-700">{r.attendance_date}</td>
                            <td className="py-1.5 px-2">
                              <span className="font-mono text-[11px] font-bold text-slate-800">{r.labour_code}</span>
                              {r.labour_name && <span className="text-[11px] text-slate-600"> · {r.labour_name}</span>}
                              {(r.skill_category || r.labour_type) && (
                                <div className="text-[10px] text-slate-400">{[r.skill_category, r.labour_type].filter(Boolean).join(" · ")}</div>
                              )}
                            </td>
                            <td className="py-1.5 px-2 text-[11px] text-slate-700">{r.category1_name || "—"}</td>
                            <td className="py-1.5 px-2 text-[11px] text-slate-700">{r.category2_name || "—"}</td>
                            <td className="py-1.5 px-2 text-[11px] text-slate-700">{r.attendance_status || "—"}</td>
                            <td className="py-1.5 px-2 text-right tabular text-slate-700">{Number(r.overtime || 0) > 0 ? Number(r.overtime).toFixed(1) : "—"}</td>
                            <td className="py-1.5 px-2 text-right tabular text-slate-700">{Number(r.units || 0)}</td>
                            <td className="py-1.5 px-2 text-right tabular text-slate-700">{Number(r.per_day_amount || 0).toFixed(2)}</td>
                            <td className="py-1.5 px-2 text-right tabular font-semibold text-slate-800">{Number(r.amount || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-50 border-t-2 border-slate-200">
                          <td colSpan={7} className="py-1.5 px-2 text-right text-[10px] uppercase tracking-wider font-bold text-slate-500">Totals</td>
                          <td className="py-1.5 px-2 text-right tabular font-bold text-slate-800" data-testid="lp-approval-total-units">{Number(viewing.doc.total_units || 0)}</td>
                          <td />
                          <td className="py-1.5 px-2 text-right tabular font-bold text-slate-900" data-testid="lp-approval-total-amount">{Number(viewing.doc.total_amount || 0).toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {viewing.doc.remark && (
                    <div className="px-2 py-1.5 border-t border-slate-100 text-[11px] text-slate-600" data-testid="lp-approval-remark">
                      Requester remark: <span className="italic">{viewing.doc.remark}</span>
                    </div>
                  )}
                </div>
              )}

              {/* ── Jun-2026 — Project BOQ approval detail block ──
                  BOQ versions carry `lines` (element rows, commercially
                  scrubbed server-side per boq_commercials permission), NOT
                  `line_items`. Approvers see the full element list before
                  approving a baseline / revision. */}
              {!viewing.orphan
                && viewing.request.module === "project_boq"
                && (viewing.doc.lines || []).length > 0 && (
                <div className="border border-slate-200" data-testid="boq-approval-lines">
                  <div className="px-2 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    BOQ Version {viewing.doc.version_number || ""} · {viewing.doc.lines.length} element{viewing.doc.lines.length === 1 ? "" : "s"}
                    {viewing.doc.commercials_visible === false ? " · commercial values hidden (boq_commercials)" : ""}
                  </div>
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    <table className="w-full border-collapse">
                      <thead className="bg-white border-b border-slate-200 sticky top-0">
                        <tr>
                          {["#", "Element", "Name / Description", "Category", "UOM", "Qty", "Scope", "Client Rate", "Taxable", "GST", "Total"].map(h => (
                            <th key={h} className="text-left py-2 px-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 whitespace-nowrap bg-white">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {viewing.doc.lines.map((l, i) => (
                          <tr key={l.id || i} className="border-b border-slate-100 align-top" data-testid={`boq-approval-line-${i}`}>
                            <td className="py-1.5 px-2 text-slate-500 tabular">{i + 1}</td>
                            <td className="py-1.5 px-2 font-mono text-[11px] font-bold text-slate-800 whitespace-nowrap">{l.element_code || "—"}</td>
                            <td className="py-1.5 px-2 text-[11px] text-slate-700">
                              <div className="font-semibold">{l.name || "—"}</div>
                              {l.description && <div className="text-[10px] text-slate-400 max-w-[280px] truncate" title={l.description}>{l.description}</div>}
                            </td>
                            <td className="py-1.5 px-2 text-[11px] text-slate-700">{l.category_name || l.category || "—"}</td>
                            <td className="py-1.5 px-2 text-[11px] text-slate-500">{l.uom_name || "—"}</td>
                            <td className="py-1.5 px-2 text-right tabular text-slate-700">{Number(l.quantity || 0)}</td>
                            <td className="py-1.5 px-2 text-[11px] text-slate-700">{l.scope_status || "—"}</td>
                            <td className="py-1.5 px-2 text-right tabular text-slate-700">{l.client_rate !== undefined ? Number(l.client_rate || 0).toFixed(2) : "—"}</td>
                            <td className="py-1.5 px-2 text-right tabular text-slate-700">{l.taxable_value !== undefined ? Number(l.taxable_value || 0).toFixed(2) : "—"}</td>
                            <td className="py-1.5 px-2 text-right tabular text-slate-500">{l.gst_value !== undefined ? Number(l.gst_value || 0).toFixed(2) : "—"}</td>
                            <td className="py-1.5 px-2 text-right tabular font-semibold text-slate-800">{l.total_value !== undefined ? Number(l.total_value || 0).toFixed(2) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {viewing.doc.summary && viewing.doc.summary.scope_value_inc_gst !== undefined && (
                    <div className="px-2 py-1.5 border-t border-slate-200 bg-slate-50 flex flex-wrap gap-4 text-[11px]" data-testid="boq-approval-summary">
                      <span>Scope (ex-GST): <strong className="tabular">{Number(viewing.doc.summary.scope_value_ex_gst || 0).toFixed(2)}</strong></span>
                      <span>GST: <strong className="tabular">{Number(viewing.doc.summary.gst_value || 0).toFixed(2)}</strong></span>
                      <span>Scope (inc-GST): <strong className="tabular">{Number(viewing.doc.summary.scope_value_inc_gst || 0).toFixed(2)}</strong></span>
                    </div>
                  )}
                </div>
              )}

              {/* ── Sprint 2026-Feb-10g — Vendor Bill (General Payment /
                  Store Rent / Salary Payment) approval detail block ──
                  These three modules share the `VendorBillBase` shape:
                  each has a `lines[]` array (NOT `line_items`) whose rows
                  are `VendorBillLine` — so the generic line_items table
                  earlier in this modal never renders for them. Approvers
                  were left staring at a bare totals card with no way to
                  see per-line vendor, expense head, ref no, amounts, or
                  remarks. This block surfaces every relevant column so
                  the approver has full context before deciding.

                  Column set is module-adaptive:
                    General Payment  → Vendor · Expense Head · Ref · Project · Amount / GST / TDS / Net · Remarks
                    Store Rent       → Vendor · Store · Rent Period · Ref · Amount / GST / TDS / Net · Remarks
                    Salary Payment   → Vendor · Category · Month · Ref · Amount / GST / TDS / Net · Remarks
              */}
              {!viewing.orphan
                && ["general_payment", "store_rent", "salary_payment"].includes(viewing.request.module)
                && (viewing.doc.lines || []).length > 0 && (() => {
                const mod = viewing.request.module;
                const isGP = mod === "general_payment";
                const isSR = mod === "store_rent";
                const isSAL = mod === "salary_payment";
                const middleColLabel =
                  isGP  ? "Expense Head"
                : isSR  ? "Store · Rent Period"
                : isSAL ? "Category · Month"
                :         "Details";
                const modLabel =
                  isGP  ? "General Payment"
                : isSR  ? "Store Rent"
                : isSAL ? "Salary Payment"
                :         mod;
                return (
                  <div className="border border-slate-200" data-testid="vb-approval-lines">
                    <div className="px-2 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                      {modLabel} · {viewing.doc.lines.length} line{viewing.doc.lines.length === 1 ? "" : "s"}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead className="bg-white border-b border-slate-200">
                          <tr>
                            {["#", "Vendor", middleColLabel, "Ref No", "Acct Period", "Project", "Amount", "GST", "− TDS", "Net", "Remarks"]
                              .filter(Boolean)
                              .map(h => (
                                <th key={h} className="text-left py-2 px-2 text-[10px] uppercase tracking-wider font-bold text-slate-500 whitespace-nowrap">{h}</th>
                              ))}
                          </tr>
                        </thead>
                        <tbody>
                          {viewing.doc.lines.map((ln, i) => {
                            const amt = Number(ln.amount || 0);
                            const gst = Number(ln.gst_amount || 0);
                            const tds = Number(ln.tds_amount || 0);
                            const net = amt + gst - tds;
                            const middleCol =
                              isGP  ? (ln.expense_head || "—")
                            : isSR  ? [ln.store_location, ln.rent_period].filter(Boolean).join(" · ") || "—"
                            : isSAL ? [ln.manpower_category, ln.month].filter(Boolean).join(" · ") || "—"
                            :         "—";
                            return (
                              <tr key={i} className="border-b border-slate-100 align-top"
                                  data-testid={`vb-approval-line-${i}`}>
                                <td className="py-1.5 px-2 text-slate-500 tabular">{i + 1}</td>
                                <td className="py-1.5 px-2 text-[11px] font-semibold text-slate-800">
                                  {ln.vendor_name || "—"}
                                </td>
                                <td className="py-1.5 px-2 text-[11px] text-slate-700">{middleCol}</td>
                                <td className="py-1.5 px-2 text-[11px] font-mono text-slate-700">{ln.ref_no || "—"}</td>
                                <td className="py-1.5 px-2 text-[11px] text-slate-700 whitespace-nowrap" data-testid={`vb-approval-period-${i}`}>
                                  {fmtPeriod((ln.reporting_period || "").trim() || (isSAL ? ln.month : isSR ? ln.rent_period : "") || (viewing.doc.voucher_date || "").slice(0, 7)) || "—"}
                                </td>
                                <td className="py-1.5 px-2 text-[11px] text-slate-600" data-testid={`vb-approval-project-${i}`}>{ln.project_name || "—"}</td>
                                <td className="py-1.5 px-2 text-right tabular font-semibold text-slate-800">{amt.toFixed(2)}</td>
                                <td className="py-1.5 px-2 text-right tabular text-slate-700">{gst > 0 ? gst.toFixed(2) : "—"}</td>
                                <td className="py-1.5 px-2 text-right tabular text-rose-600">{tds > 0 ? tds.toFixed(2) : "—"}</td>
                                <td className="py-1.5 px-2 text-right tabular font-bold text-slate-900">{net.toFixed(2)}</td>
                                <td className="py-1.5 px-2 text-[10px] text-slate-500 max-w-[220px]">
                                  {ln.remarks ? (
                                    <div className="whitespace-pre-wrap" title={ln.remarks}>{ln.remarks}</div>
                                  ) : (
                                    <span className="text-slate-300">—</span>
                                  )}
                                  {ln.attachment_url && ln.attachment_name && (
                                    <a href={ln.attachment_url} target="_blank" rel="noopener noreferrer"
                                       className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] text-blue-600 hover:underline"
                                       data-testid={`vb-approval-line-attachment-${i}`}>
                                      📎 {ln.attachment_name}
                                    </a>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
              {/* Jul-2026 — Vendor Advance approval detail: Purpose +
                  Actual Beneficiary always visible to the approver. */}
              {!viewing.orphan && viewing.request.module === "vendor_advance" && (
                <div className="border border-slate-200 bg-blue-50/30 p-2.5" data-testid="va-approval-details">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Purpose of Advance</div>
                      <div className="text-[12px] text-slate-800 whitespace-pre-wrap" data-testid="va-approval-purpose">
                        {(viewing.doc?.purpose || "").trim() || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                        Actual Beneficiary <span className="text-[9px] font-normal italic">(payment routed via)</span>
                      </div>
                      <div className="text-[12px] text-slate-800 font-mono" data-testid="va-approval-beneficiary">
                        {(viewing.doc?.actual_beneficiary || "").trim() || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {!viewing.orphan
                && viewing.request.module !== "vendor_master"
                && ((viewing.doc?.notes || "").trim().length > 0
                    || (viewing.request.module !== "vendor_advance"
                        && (viewing.doc?.actual_beneficiary || "").trim().length > 0)) && (
                <div className="border border-slate-200 bg-amber-50/30 p-2.5" data-testid="approval-doc-remarks">
                  {(viewing.doc.notes || "").trim().length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                        Remark from voucher creator
                      </div>
                      <div className="text-[12px] text-slate-800 whitespace-pre-wrap" data-testid="approval-doc-notes">
                        {viewing.doc.notes}
                      </div>
                    </div>
                  )}
                  {viewing.request.module !== "vendor_advance" && (viewing.doc.actual_beneficiary || "").trim().length > 0 && (
                    <div className={(viewing.doc.notes || "").trim().length > 0 ? "mt-2 pt-2 border-t border-amber-200/60" : ""}>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                        Actual Beneficiary <span className="text-[9px] font-normal italic">(payment routed via)</span>
                      </div>
                      <div className="text-[12px] text-slate-800 font-mono" data-testid="approval-doc-actual-beneficiary">
                        {viewing.doc.actual_beneficiary}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className={`grid grid-cols-2 gap-2 text-xs pt-2 border-t border-slate-200 ${(viewing.orphan || ["vendor_master", "stock_adjustment_voucher"].includes(viewing.request.module)) ? "hidden" : ""}`}>
                <div className="text-slate-500">Subtotal</div><div className="text-right tabular">{Number(viewing.doc?.subtotal || viewing.doc?.est_subtotal || viewing.doc?.total_amount || viewing.doc?.total_invoice || viewing.doc?.total_value || viewing.doc?.advance_amount || 0).toFixed(2)}</div>
                {Number(viewing.doc?.cgst_amount || 0) > 0 && (<>
                  <div className="text-slate-500">CGST</div><div className="text-right tabular">{Number(viewing.doc.cgst_amount || 0).toFixed(2)}</div>
                  <div className="text-slate-500">SGST</div><div className="text-right tabular">{Number(viewing.doc.sgst_amount || 0).toFixed(2)}</div>
                </>)}
                {Number(viewing.doc?.igst_amount || 0) > 0 && (<>
                  <div className="text-slate-500">IGST</div><div className="text-right tabular">{Number(viewing.doc.igst_amount || 0).toFixed(2)}</div>
                </>)}
                {Number(viewing.doc?.total_gst || 0) > 0 && (<>
                  <div className="text-slate-500">GST</div><div className="text-right tabular">{Number(viewing.doc.total_gst || 0).toFixed(2)}</div>
                </>)}
                {Number(viewing.doc?.est_tax_total || 0) > 0 && (<>
                  <div className="text-slate-500">Est. GST</div><div className="text-right tabular">{Number(viewing.doc.est_tax_total || 0).toFixed(2)}</div>
                </>)}
                {Number(viewing.doc?.total_tds || 0) > 0 && (<>
                  <div className="text-slate-500">− TDS</div><div className="text-right tabular text-rose-600">{Number(viewing.doc.total_tds || 0).toFixed(2)}</div>
                </>)}
                {Number(viewing.doc?.total_deductions || 0) > 0 && (<>
                  <div className="text-slate-500">− Deductions</div><div className="text-right tabular text-rose-600">{Number(viewing.doc.total_deductions || 0).toFixed(2)}</div>
                </>)}
                {Number(viewing.doc?.total_holds || 0) > 0 && (<>
                  <div className="text-slate-500">− Holds</div><div className="text-right tabular text-amber-700">{Number(viewing.doc.total_holds || 0).toFixed(2)}</div>
                </>)}
                {Number(viewing.doc?.total_advance_adjusted || 0) > 0 && (<>
                  <div className="text-slate-500">− Advance Adjusted</div><div className="text-right tabular text-rose-600">{Number(viewing.doc.total_advance_adjusted || 0).toFixed(2)}</div>
                </>)}
                <div className="text-slate-900 font-bold border-t border-slate-200 pt-2 mt-1">Total</div>
                <div data-testid="approval-doc-total" className="text-right tabular font-bold text-slate-900 border-t border-slate-200 pt-2 mt-1">
                  {Number(
                    viewing.doc?.total_net_payable
                    ?? viewing.doc?.grand_total
                    ?? viewing.doc?.total_amount
                    ?? viewing.doc?.total_value
                    ?? viewing.doc?.est_total
                    ?? viewing.doc?.advance_amount
                    ?? viewing.doc?.net_payable
                    ?? viewing.doc?.amount
                    ?? 0
                  ).toFixed(2)}
                </div>
              </div>

              {!viewing.orphan && (viewing.doc.attachments || []).length > 0 && (
                <div className="pt-3 border-t border-slate-200" data-testid="approval-doc-attachments">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1.5">
                    Supporting Documents · {viewing.doc.attachments.length} file(s)
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {(viewing.doc.attachments || []).map((a, i) => (
                      viewing.request.module === "project_purchase_requisition" ? (
                        // PR attachments have no public URL — open via the
                        // authenticated download endpoint.
                        <button key={a.id || a.stored_as || i} type="button"
                          onClick={() => blobOpen(`/projects/${viewing.doc.project_id}/prs/${viewing.doc.id}/attachments/${a.id}`, a.filename)}
                          data-testid={`approval-attachment-${i}`}
                          className="border border-slate-200 bg-white px-2 py-1.5 flex items-center gap-2 text-left hover:border-[#F97316]">
                          <span className="text-[11px] text-slate-700 hover:text-[#F97316] truncate flex-1" title={a.filename}>
                            📎 {a.filename || "attachment"}
                          </span>
                          <span className="text-[10px] text-slate-400 tabular flex-shrink-0">
                            {Math.max(1, Math.round((a.size || 0) / 1024))} KB
                          </span>
                        </button>
                      ) : (
                        <AttachmentChip key={a.id || a.stored_as || i} a={a} testid={`approval-attachment-${i}`}/>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DocSummary({ doc }) {
  // Warehouse Transfers (module="warehouse_transfer") carry both the
  // source (`from_warehouse_name`) and destination (`to_warehouse_name`)
  // warehouse. Approvers need to see BOTH before deciding, so we surface
  // them here. Other modules do not carry these fields, so the rows are
  // simply filtered out below.
  //
  // Issue Vouchers (module="issue_voucher") carry an "Issued To" recipient
  // identified by `issued_to_type` ("Member" | "Client" | "Vendor" | "Other")
  // combined with either `issued_to_name` (for the three registered types)
  // or `issued_to_free_text` (for "Other" ad-hoc recipients). Approvers
  // need to see who the material is going to — otherwise the modal is
  // largely blank for IV approvals and the approver has to open the
  // source document to make sense of the request.
  const issuedToName = (doc.issued_to_free_text || doc.issued_to_name || "").toString().trim();
  const issuedToType = (doc.issued_to_type || "").toString().trim();
  const issuedToLabel = issuedToName
    ? (issuedToType ? `${issuedToType} · ${issuedToName}` : issuedToName)
    : "";
  // Return Vouchers (module="return_voucher") are the mirror image of IV
  // and carry a "Returned From" party under `returned_from_type` +
  // `returned_from_name` / `returned_from_free_text`. Same rules apply.
  const returnedFromName = (doc.returned_from_free_text || doc.returned_from_name || "").toString().trim();
  const returnedFromType = (doc.returned_from_type || "").toString().trim();
  const returnedFromLabel = returnedFromName
    ? (returnedFromType ? `${returnedFromType} · ${returnedFromName}` : returnedFromName)
    : "";
  const rows = [
    ["Date", doc.estimate_date || doc.invoice_date || doc.voucher_date || doc.transfer_date],
    ["Branch", doc.branch_name],
    ["From Warehouse", doc.from_warehouse_name],
    ["To Warehouse", doc.to_warehouse_name],
    ["Issued To", issuedToLabel],
    ["Returned From", returnedFromLabel],
    ["Client / Vendor", doc.client_name || doc.vendor_name || doc.party_name],
    ["Project", doc.project_name],
    ["GST Number", doc.gst_number],
    ["GST Type", doc.sales_gst_type],
    ["Created By", formatUser(doc.created_by, "")],
  ].filter(([_, v]) => v);
  // Remark / Note — sourced from whichever field the underlying module
  // persists into. Different documents use different field names:
  //   • Estimates / Sales / Purchase Invoices, Issue/Return → `notes`
  //   • Vendor Advance, BPV, Salary/General Payment etc.   → `remarks`
  //   • Expense Voucher, Bank Ledger entries               → `narration`
  //   • Free-text fallback                                  → `description`
  // We pick the first non-empty one so the approver always sees what the
  // requester wrote, regardless of module.
  const remark = (doc.notes || doc.remarks || doc.narration || doc.description || "").toString().trim();
  // Accounting Period banner — shown for EVERY voucher the approver views.
  // Priority: header reporting_period override → distinct line-level
  // overrides (GP/SR/Salary carry it per line) → document date month.
  const docDate = (doc.estimate_date || doc.invoice_date || doc.voucher_date || doc.transfer_date || doc.payment_date || doc.bill_date || "").toString();
  const docMonth = docDate.slice(0, 7);
  const headerPeriod = (doc.reporting_period || "").toString().trim();
  const linePeriods = [...new Set((doc.lines || [])
    .map((l) => (l.reporting_period || "").toString().trim()).filter(Boolean))];
  const effectivePeriod = headerPeriod || (linePeriods.length === 1 ? linePeriods[0] : "") || docMonth;
  const hasOverride = !!headerPeriod || linePeriods.length > 0;
  const differsFromDate = hasOverride && effectivePeriod && docMonth && effectivePeriod !== docMonth;
  return (
    <div className="space-y-3">
      {effectivePeriod && (
        <div className={`flex flex-wrap items-center gap-2 px-3 py-2 border ${
          differsFromDate ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-slate-50"
        }`} data-testid="approval-doc-accounting-period">
          <CalendarDays size={14} className={differsFromDate ? "text-amber-600" : "text-slate-500"} />
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Accounting Period</span>
          {linePeriods.length > 1 ? (
            <span className="font-bold text-slate-900 text-xs" title={linePeriods.join(", ")}>
              {linePeriods.map(fmtPeriod).join(" · ")} <span className="font-normal text-slate-500">(varies by line)</span>
            </span>
          ) : (
            <span className="font-bold text-slate-900 text-xs">{fmtPeriod(effectivePeriod)}</span>
          )}
          <span className={`text-[10px] ${differsFromDate ? "text-amber-700 font-bold" : "text-slate-400"}`}>
            {differsFromDate
              ? `override — differs from document date (${fmtPeriod(docMonth)})`
              : hasOverride ? "set by voucher creator" : "from document date"}
          </span>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {rows.map(([k, v]) => {
          const slug = k.toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
          return (
            <div key={k} className="text-xs" data-testid={`approval-doc-field-${slug}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
              <div className="text-slate-800 font-medium truncate" title={v}>{v}</div>
            </div>
          );
        })}
      </div>
      {remark && (
        <div className="pt-2 border-t border-slate-200" data-testid="approval-doc-remark">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Remark / Note</div>
          <div className="text-xs text-slate-800 whitespace-pre-wrap leading-relaxed">{remark}</div>
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Sprint 4.11.18 — Vendor Master approval view
// ────────────────────────────────────────────────────────────────────────────
// Dedicated summary panel rendered inside the Approval Inbox modal when the
// pending request is a `vendor_master` create / edit. The approver needs to
// see the COMPLETE vendor profile (statutory IDs, contact, POCs, banks,
// attachments) before deciding — the generic DocSummary only has voucher
// fields and would leave the modal mostly empty.

// Authenticated blob open — PR attachments have no public URL; fetch via API
// (token attached) and open in a new tab (fallback: download).
async function blobOpen(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const w = window.open(url, "_blank");
    if (!w) {
      const a = document.createElement("a");
      a.href = url; a.download = filename || "attachment"; a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Could not open the attachment");
  }
}

function FieldCell({ label, value, mono, testid }) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="text-xs" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-slate-800 font-medium break-words ${mono ? "font-mono text-[11px]" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function VendorSummary({ doc }) {
  if (!doc) return null;
  const pocs = doc.pocs || [];
  const banks = doc.bank_accounts || [];
  const tags = doc.tags || [];
  return (
    <div className="space-y-4" data-testid="vendor-master-approval-view">
      {/* ── Identity ─────────────────────────────────────────────── */}
      <section>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 mb-2">
          Identity &amp; Statutory
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 bg-slate-50 px-4 py-3 border border-slate-200">
          <FieldCell label="Vendor Code"   value={doc.vendor_code} mono testid="vm-vendor-code" />
          <FieldCell label="Vendor Name"   value={doc.name} testid="vm-name" />
          <FieldCell label="Branch"        value={doc.branch_name} testid="vm-branch" />
          <FieldCell label="GST Number"    value={doc.gst_number ? `${doc.gst_number}${doc.gst_verified ? " ✓" : ""}` : ""} mono testid="vm-gst" />
          <FieldCell label="PAN"           value={doc.pan} mono testid="vm-pan" />
          <FieldCell label="TDS Rate (%)"  value={doc.tds_rate != null ? Number(doc.tds_rate).toFixed(2) : ""} testid="vm-tds" />
          <FieldCell label="Vendor Tag"    value={doc.skill_type} testid="vm-skill" />
          <FieldCell label="Vendor Type"   value={doc.vendor_type} testid="vm-vendor-type" />
          <FieldCell label="MSME Status"   value={doc.msme_status} testid="vm-msme" />
          <FieldCell label="Opening Bal."  value={doc.opening_balance != null ? `₹ ${Number(doc.opening_balance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : ""} testid="vm-opening-bal" />
          <FieldCell label="Active"        value={doc.is_active ? "Yes" : "No"} testid="vm-active" />
          <FieldCell label="Approval"      value={doc.approval_status} testid="vm-approval" />
        </div>
      </section>

      {/* ── Contact ──────────────────────────────────────────────── */}
      {(doc.phone || doc.email || doc.address || doc.contact_person) && (
        <section>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 mb-2">Contact</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 bg-slate-50 px-4 py-3 border border-slate-200">
            <FieldCell label="Mobile"          value={doc.phone} testid="vm-phone" />
            <FieldCell label="Email"           value={doc.email} testid="vm-email" />
            <FieldCell label="Contact Person"  value={doc.contact_person} testid="vm-contact-person" />
            <div className="md:col-span-3">
              <FieldCell label="Address" value={doc.address} testid="vm-address" />
            </div>
          </div>
        </section>
      )}

      {/* ── Points of Contact ───────────────────────────────────── */}
      {pocs.length > 0 && (
        <section>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 mb-2">
            Points of Contact &nbsp;<span className="text-slate-400 normal-case font-normal">({pocs.length})</span>
          </div>
          <div className="border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["#", "Name", "Designation", "Mobile", "Email", "Department"].map(h => (
                    <th key={h} className="text-left py-1.5 px-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pocs.map((p, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`vm-poc-row-${i}`}>
                    <td className="py-1.5 px-2 text-slate-500">{i + 1}</td>
                    <td className="py-1.5 px-2 text-slate-800 font-medium">{p.name || "—"}</td>
                    <td className="py-1.5 px-2 text-slate-700">{p.designation || "—"}</td>
                    <td className="py-1.5 px-2 text-slate-700">{p.mobile || "—"}</td>
                    <td className="py-1.5 px-2 text-slate-700">{p.email || "—"}</td>
                    <td className="py-1.5 px-2 text-slate-500">{p.department || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Bank Accounts ───────────────────────────────────────── */}
      {banks.length > 0 && (
        <section>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 mb-2">
            Bank Accounts &nbsp;<span className="text-slate-400 normal-case font-normal">({banks.length})</span>
          </div>
          <div className="border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["#", "Holder", "Bank", "Account No.", "IFSC", "Branch", "Default"].map(h => (
                    <th key={h} className="text-left py-1.5 px-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {banks.map((b, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`vm-bank-row-${i}`}>
                    <td className="py-1.5 px-2 text-slate-500">{i + 1}</td>
                    <td className="py-1.5 px-2 text-slate-800">{b.account_holder || "—"}</td>
                    <td className="py-1.5 px-2 text-slate-700">{b.bank_name || "—"}</td>
                    <td className="py-1.5 px-2 text-slate-700 font-mono">{b.account_number || "—"}</td>
                    <td className="py-1.5 px-2 text-slate-700 font-mono">{b.ifsc_code || "—"}</td>
                    <td className="py-1.5 px-2 text-slate-500">{b.branch || "—"}</td>
                    <td className="py-1.5 px-2 text-slate-500">{b.is_default ? "Default" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Tags ────────────────────────────────────────────────── */}
      {tags.length > 0 && (
        <section>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 mb-1">Tags</div>
          <div className="flex flex-wrap gap-1">
            {tags.map((t, i) => (
              <span key={i} className="text-[10px] uppercase tracking-wider bg-orange-100 text-orange-800 px-2 py-0.5 font-bold">
                {t.label || t}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* ── Audit ───────────────────────────────────────────────── */}
      <section className="pt-2 border-t border-slate-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <FieldCell label="Created By"  value={formatUser(doc.created_by, "")} testid="vm-created-by" />
          <FieldCell label="Created At"  value={doc.created_at ? new Date(doc.created_at).toLocaleString("en-IN") : ""} testid="vm-created-at" />
          <FieldCell label="Updated At"  value={doc.updated_at ? new Date(doc.updated_at).toLocaleString("en-IN") : ""} testid="vm-updated-at" />
          <FieldCell label="Approved By" value={formatUser(doc.approved_by, "")} testid="vm-approved-by" />
        </div>
      </section>
    </div>
  );
}